import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from "react";
import {
  getSuppliers,
  addItem,
  addInwardEntry,
  addSalesEntry,
  updateInwardEntry,
  updateSalesEntry,
  deleteInwardEntry,
  deleteInwardPayment,
  getCustomerByPhone,
  getCustomers,
  getInwardEntries,
  addSupplierPayment,
  updateInwardPayment,
  getItems,
  getSalesEntries,
  getMyBillActionRequests,
  createBillActionRequest,
  getApiBaseRoot,
  saveReceiptHtml,
} from "./storage";
import "./StockInward.css";
import PortalDropdown from "./components/PortalDropdown";
import { formatDisplayDate, toComparableYmd } from "./utils/dateFormat";
import { getSettlementSummary, isPaymentSettlement } from "./utils/settlement";
import { buildReceiptHtml as buildSalesReceiptHtml } from "./pages/Billing";
import { confirmAndCreateQuickItem } from "./utils/quickItem";

function emptyRow() {
  return {
    material: "",
    qty: "",
    rate: "",
    amount: 0,
    stockQty: 0,
    closingQty: 0,
    closingRate: 0,
    closingAmount: 0,
    retailQty: 0,
    retailRate: 0,
    retailAmount: 0,
  };
}

const DEFAULT_ENTRY_ROWS = 5;
const SUPPLIER_MODAL_PAGE_SIZE = 10;
const SAVED_ENTRIES_PAGE_SIZE = 10;
const EVENING_SALES_PAYMENT_TYPES = ["Any", "UPI", "Cash", "Credit", "Cheque", "Acc Transfer", "Razorpay", "Card"];
const INWARD_ITEMS_HEADER_STYLE = { background: "#dbeafe", color: "#1e3a8a" };
const INWARD_ITEMS_CELL_STYLE = { background: "#f8fbff" };
const CLOSING_ITEMS_HEADER_STYLE = { background: "#fef3c7", color: "#92400e" };
const CLOSING_ITEMS_CELL_STYLE = { background: "#fffaf0" };
const RETAIL_ITEMS_HEADER_STYLE = { background: "#dcfce7", color: "#166534" };
const RETAIL_ITEMS_CELL_STYLE = { background: "#f0fdf4" };

function makeDefaultRows() {
  return Array.from({ length: DEFAULT_ENTRY_ROWS }, () => emptyRow());
}

function makeSupplierPaidForm() {
  return {
    date: new Date().toISOString().slice(0, 10),
    amount: "",
  };
}

function emptyEveningSaleRow() {
  return { material: "", qty: "", rate: "", amount: 0, closingStock: 0 };
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function readClosingQty(item) {
  return firstFiniteNumber(item?.closingQty, item?.closing_qty);
}

function readRetailQty(item) {
  return firstFiniteNumber(item?.retailQty, item?.retail_qty);
}

function readRetailRate(item) {
  return firstFiniteNumber(item?.retailRate, item?.retail_rate);
}

function readRetailAmount(item) {
  const explicit = firstFiniteNumber(item?.retailAmount, item?.retail_amount);
  if (explicit > 0) return explicit;
  return +(readRetailQty(item) * readRetailRate(item)).toFixed(2);
}

function normalizeMaterialKey(value) {
  return String(value || "").trim().toLowerCase();
}

function calculateRetailQty({ stockQty, outwardQty, closingQty, activeCategory }) {
  const normalizedCategory = String(activeCategory || "").trim().toLowerCase();
  if (normalizedCategory === "stall") {
    return Math.max(0, stockQty - closingQty);
  }
  return Math.max(0, stockQty - outwardQty - closingQty);
}

function buildDistributedOutwardRows({ entries, salesEntries, activeCategory, selectedDate }) {
  const outwardByMaterial = new Map();

  for (const sale of Array.isArray(salesEntries) ? salesEntries : []) {
    if (String(sale?.category || "").trim() !== String(activeCategory || "").trim()) continue;
    if (toComparableYmd(sale?.saleDate) !== selectedDate) continue;
    for (const item of Array.isArray(sale?.items) ? sale.items : []) {
      const materialKey = normalizeMaterialKey(item?.material);
      if (!materialKey) continue;
      outwardByMaterial.set(materialKey, (outwardByMaterial.get(materialKey) || 0) + (Number(item?.qty) || 0));
    }
  }

  const nextRows = [];
  for (const en of Array.isArray(entries) ? entries : []) {
    if (toComparableYmd(en?.createdAt) !== selectedDate) continue;
    const items = Array.isArray(en?.items) ? en.items : [];
    items.forEach((it, idx) => {
      const material = String(it?.material || "").trim();
      if (!material) return;
      const materialKey = normalizeMaterialKey(material);
      const stockQty = Number(it?.stockQty ?? it?.stock_qty ?? it?.qty ?? 0) || 0;
      const closingQty = readClosingQty(it);
      const retailRate = readRetailRate(it);
      const remainingOutward = outwardByMaterial.get(materialKey) || 0;
      const outwardQty = Math.max(0, Math.min(stockQty, remainingOutward));
      const retailQty = calculateRetailQty({ stockQty, outwardQty, closingQty, activeCategory });
      outwardByMaterial.set(materialKey, Math.max(0, remainingOutward - outwardQty));

      nextRows.push({
        key: `${String(en.id)}-${idx}`,
        entryId: en.id,
        itemIndex: idx,
        supplierName: en?.supplierName || "",
        material,
        stockQty: +stockQty.toFixed(3),
        outwardQty: +outwardQty.toFixed(3),
        closingQty: closingQty > 0 ? +closingQty.toFixed(3) : "",
        retailQty: +retailQty.toFixed(3),
        retailRate: retailRate > 0 ? +retailRate.toFixed(2) : "",
        retailAmount: +(retailQty * retailRate).toFixed(2),
      });
    });
  }

  nextRows.sort((a, b) => {
    const supplierCmp = String(a.supplierName || "").localeCompare(String(b.supplierName || ""), undefined, { sensitivity: "base" });
    if (supplierCmp !== 0) return supplierCmp;
    const materialCmp = String(a.material || "").localeCompare(String(b.material || ""), undefined, { sensitivity: "base" });
    if (materialCmp !== 0) return materialCmp;
    return Number(a.itemIndex || 0) - Number(b.itemIndex || 0);
  });

  return nextRows;
}

function buildInlineClosingRows({
  rows,
  visibleEntries,
  salesEntries,
  activeCategory,
  entryDate,
  editingEntry,
  supplierName,
}) {
  const normalizedDate = String(entryDate || "").trim();
  const comparableDate = toComparableYmd(normalizedDate);
  const normalizedRows = Array.isArray(rows) ? rows : [];

  if (!normalizedRows.length) {
    return [];
  }

  const withBaseAmounts = normalizedRows.map((row) => {
    const qty = Number(row?.qty) || 0;
    const rate = Number(row?.rate) || 0;
    return {
      ...row,
      amount: +(qty * rate).toFixed(2),
    };
  });

  if (String(activeCategory || "").trim().toLowerCase() === "stall") {
    return withBaseAmounts.map((row) => {
      const stockQty = Number(row?.qty) || 0;
      const closingQty = Number(row?.closingQty) || 0;
      const retailRate = Number(row?.retailRate) || 0;
      const retailQty = calculateRetailQty({
        stockQty,
        outwardQty: 0,
        closingQty,
        activeCategory,
      });
      return {
        ...row,
        stockQty: +stockQty.toFixed(3),
        retailQty: +retailQty.toFixed(3),
        retailAmount: +(retailQty * retailRate).toFixed(2),
      };
    });
  }

  if (String(activeCategory || "").trim().toLowerCase() !== "market" || !comparableDate) {
    return withBaseAmounts;
  }

  const draftEntryId = String(editingEntry?.id || "__draft_inward__");
  const otherEntries = (Array.isArray(visibleEntries) ? visibleEntries : []).filter(
    (entry) => String(entry?.id || "") !== String(editingEntry?.id || "")
  );
  const draftEntry = {
    id: draftEntryId,
    supplierName: supplierName || editingEntry?.supplierName || "",
    createdAt: `${comparableDate}T00:00:00.000Z`,
    items: withBaseAmounts
      .filter((row) => row.material || row.qty || row.rate || row.closingQty || row.retailRate)
      .map((row) => ({
        material: String(row?.material || ""),
        qty: Number(row?.qty) || 0,
        stockQty: Number(row?.qty) || 0,
        closingQty: Number(row?.closingQty) || 0,
        retailRate: Number(row?.retailRate) || 0,
      })),
  };

  const combinedEntries = [...otherEntries, draftEntry].sort((a, b) =>
    String(b?.createdAt || "").localeCompare(String(a?.createdAt || ""))
  );
  const distributedRows = buildDistributedOutwardRows({
    entries: combinedEntries,
    salesEntries,
    activeCategory,
    selectedDate: comparableDate,
  });
  const inlineRowByIndex = new Map();
  for (const distributedRow of distributedRows) {
    if (String(distributedRow?.entryId || "") !== draftEntryId) continue;
    inlineRowByIndex.set(Number(distributedRow?.itemIndex || 0), distributedRow);
  }

  return withBaseAmounts.map((row, index) => {
    const distributedRow = inlineRowByIndex.get(index);
    const stockQty = distributedRow ? Number(distributedRow.stockQty) || 0 : Number(row?.qty) || 0;
    const closingQty = Number(row?.closingQty) || 0;
    const retailRate = Number(row?.retailRate) || 0;
    const outwardQty = distributedRow ? Number(distributedRow.outwardQty) || 0 : 0;
    const retailQty = calculateRetailQty({
      stockQty,
      outwardQty,
      closingQty,
      activeCategory,
    });
    return {
      ...row,
      stockQty: +stockQty.toFixed(3),
      retailQty: +retailQty.toFixed(3),
      retailAmount: +(retailQty * retailRate).toFixed(2),
    };
  });
}

function hasInwardDraftRowValue(row) {
  return Boolean(
    String(row?.material || "").trim() ||
    Number(row?.qty) ||
    Number(row?.rate) ||
    Number(row?.closingQty) ||
    Number(row?.retailRate)
  );
}

function escapePrintHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function resolvePublicAsset(path) {
  const cleanPath = String(path || "").replace(/^\/*/, "");
  const base = process.env.PUBLIC_URL || "";
  const basePath = String(base).replace(/\/+$/, "");
  const relative = `${basePath}/${cleanPath}`.replace(/\/{2,}/g, "/");
  try {
    return new URL(relative, window.location.href).toString();
  } catch (e) {
    return relative;
  }
}

function formatPurchaseReceiptNo(entry) {
  const id = String(entry?.id || entry?.entryId || "").trim();
  return id ? id.slice(-4).toUpperCase() : "-";
}

function formatPurchaseReceiptTime(value) {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function buildPurchaseReceiptHtml(entry, balanceAmount) {
  const items = (Array.isArray(entry?.items) ? entry.items : []).map((item) => ({
    material: item?.material || "",
    qty: Number(item?.qty) || 0,
    rate: Number(item?.rate) || 0,
    amount: Number(item?.amount) || 0,
  }));
  return buildSalesReceiptHtml({
    logoUrl: resolvePublicAsset("Logo.png"),
    qrUrl: resolvePublicAsset("alifqr.PNG"),
    billNo: formatPurchaseReceiptNo(entry),
    billDate: formatDisplayDate(entry?.createdAt),
    billTime: formatPurchaseReceiptTime(entry?.createdAt),
    customerLines: [entry?.supplierName || "-"],
    items,
    totalQty: Number(entry?.totalQty) || 0,
    discountAmount: 0,
    totalAmount: Number(entry?.total) || 0,
    balanceAmount,
    numberOfBoxes:
      String(entry?.category || "General") === "General" ? null : entry?.numberOfBoxes ?? null,
    paymentMode: Number(balanceAmount || 0) > 0 ? "Credit" : "Paid",
    detailTitle: "Supplier Details:",
    detailFallback: "Supplier not provided",
    documentLabel: "Purchase No:",
    documentTitle: `Material Purchase Receipt ${formatPurchaseReceiptNo(entry)}`,
  });
}

function normalizePhoneCandidate(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (/^[6-9]\d{9}$/.test(digits)) return `91${digits}`;
  if (/^0[6-9]\d{9}$/.test(digits)) return `91${digits.slice(1)}`;
  if (/^91[6-9]\d{9}$/.test(digits)) return digits;
  if (/^091[6-9]\d{9}$/.test(digits)) return digits.slice(1);
  if (/^0091[6-9]\d{9}$/.test(digits)) return digits.slice(2);
  if (digits.length >= 10 && digits.length <= 15) {
    return digits.startsWith("00") ? digits.slice(2) : digits;
  }
  return "";
}

function addDaysYmd(dateText, days) {
  const raw = String(dateText || "").trim();
  let date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map((value) => Number(value) || 0);
    date = new Date(year, Math.max(0, month - 1), day || 1);
  } else {
    const fallback = new Date();
    date = new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
  }
  date.setDate(date.getDate() + Number(days || 0));
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function extractPhone(value) {
  const text = String(value || "");
  if (!text.trim()) return "";
  const tokenPattern = /(?:\+?\d[\d\s().-]{7,}\d)/g;
  const matches = text.match(tokenPattern) || [];
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const normalized = normalizePhoneCandidate(matches[i]);
    if (normalized) return normalized;
  }
  return "";
}

function buildInwardWhatsAppText({
  supplierName,
  entryDate,
  numberOfBoxes,
  items,
  totalQty,
  totalAmount,
  balanceAmount,
  receiptLink,
}) {
  const header = `*ARF Seafoods*%0A*Material Purchase*`;
  const supplierLine = `%0A*Supplier:* ${encodeURIComponent(supplierName || "-")}`;
  const dateLine = `%0A*Date:* ${encodeURIComponent(entryDate || "-")}`;
  const boxesLine =
    numberOfBoxes !== null && numberOfBoxes !== undefined && numberOfBoxes !== ""
      ? `%0A*Number of Boxes:* ${encodeURIComponent(numberOfBoxes)}`
      : "";
  const itemLines = (Array.isArray(items) ? items : [])
    .slice(0, 15)
    .map((it, idx) =>
      encodeURIComponent(
        `${idx + 1}. ${it.material || "-"} | Qty: ${(Number(it.qty) || 0).toFixed(2)} | Rate: ${(
          Number(it.rate) || 0
        ).toFixed(2)} | Amt: ${(Number(it.amount) || 0).toFixed(2)}`
      )
    )
    .join("%0A");
  const totals = `%0A*Total Qty:* ${(Number(totalQty) || 0).toFixed(2)}%0A*Total Amt:* ${(
    Number(totalAmount) || 0
  ).toFixed(2)}%0A*Balance Amount:* ${(Number(balanceAmount) || 0).toFixed(2)}`;
  const linkText = receiptLink ? `%0A%0AView receipt: ${receiptLink}` : "";
  return `${header}${supplierLine}${dateLine}${boxesLine}%0A%0A${itemLines}${itemLines ? "%0A" : ""}${totals}${linkText}`;
}

function normalizeEveningSalesPaymentType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "any") return "Any";
  if (raw === "upi") return "UPI";
  if (raw === "cash") return "Cash";
  if (raw === "cheque" || raw === "check") return "Cheque";
  if (raw === "acc transfer" || raw === "account transfer" || raw === "bank transfer") return "Acc Transfer";
  if (raw === "razorpay") return "Razorpay";
  if (raw === "card" || raw === "credit card" || raw === "debit card") return "Card";
  if (raw === "credit") return "Credit";
  return "Any";
}

function parseCustomerDetails(value) {
  const lines = String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return {
    name: lines[0] || "",
    phone: lines[1] || "",
    address: lines.slice(2).join(", "),
  };
}

export default function StockInward({ category: lockedCategory, title, currentUser }) {
  const activeCategory = lockedCategory || "General";
  const isAdmin = (currentUser?.role || "") === "admin";
  const showClosingStockOption = activeCategory === "Stall" || activeCategory === "Market";
  const showInlineClosingStockFields = activeCategory === "Stall" || activeCategory === "Market";
  const showNumberOfBoxes = activeCategory !== "General";
  const hideInlinePurchaseColumns = showInlineClosingStockFields;
  const showEveningSalesOption = activeCategory === "Stall";
  const fixedSupplier =
    activeCategory === "Stall" || activeCategory === "Market" ? "ARF Seafoods" : null;
  const [rows, setRows] = useState(() => makeDefaultRows());
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [supplierName, setSupplierName] = useState(fixedSupplier || "");
  const [numberOfBoxes, setNumberOfBoxes] = useState("");
  const boxesInputRef = useRef(null);
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState({});
  const materialRefs = useRef({});
  const [materialRects, setMaterialRects] = useState({});
  const [openMaterialIndex, setOpenMaterialIndex] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryDeleting, setEntryDeleting] = useState({});
  const [entryRequesting, setEntryRequesting] = useState({});
  const [myRequests, setMyRequests] = useState([]);
  const [salesEntries, setSalesEntries] = useState([]);
  const [activeMaterialSuggestionIndex, setActiveMaterialSuggestionIndex] = useState(-1);
  const [savedSupplierFilter, setSavedSupplierFilter] = useState("");
  const [supplierDetailsModal, setSupplierDetailsModal] = useState({ open: false, metric: "qty" });
  const [supplierDetailsPage, setSupplierDetailsPage] = useState(1);
  const [supplierPaidForm, setSupplierPaidForm] = useState(() => makeSupplierPaidForm());
  const [supplierPaidSaving, setSupplierPaidSaving] = useState(false);
  const [supplierPaidEdit, setSupplierPaidEdit] = useState({ paymentId: "", date: "", amount: "" });
  const [supplierPaidUpdating, setSupplierPaidUpdating] = useState(false);
  const [supplierPaidDeleting, setSupplierPaidDeleting] = useState({});
  const [savedEntriesTab, setSavedEntriesTab] = useState("inward");
  const [savedEntriesPage, setSavedEntriesPage] = useState(1);
  const [eveningSalesModalOpen, setEveningSalesModalOpen] = useState(false);
  const [eveningSalesSaving, setEveningSalesSaving] = useState(false);
  const [editingEveningSaleEntry, setEditingEveningSaleEntry] = useState(null);
  const [eveningSaleDate, setEveningSaleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [eveningSalePaymentType, setEveningSalePaymentType] = useState("Any");
  const [eveningCustomerPhone, setEveningCustomerPhone] = useState("");
  const [eveningCustomerName, setEveningCustomerName] = useState("");
  const [eveningCustomerAddress, setEveningCustomerAddress] = useState("");
  const [eveningCustomerSuggestions, setEveningCustomerSuggestions] = useState([]);
  const [eveningCustomerSuggestionIndex, setEveningCustomerSuggestionIndex] = useState(-1);
  const [eveningCustomerLookup, setEveningCustomerLookup] = useState({ status: "", message: "" });
  const [eveningSaleRows, setEveningSaleRows] = useState([emptyEveningSaleRow()]);
  const [eveningOpenMaterialIndex, setEveningOpenMaterialIndex] = useState(null);
  const [eveningActiveMaterialSuggestionIndex, setEveningActiveMaterialSuggestionIndex] = useState(-1);
  const [closingStockModalOpen, setClosingStockModalOpen] = useState(false);
  const [closingStockSourceDate, setClosingStockSourceDate] = useState("");
  const [closingStockSourceRows, setClosingStockSourceRows] = useState([]);
  const [closingStockSourceError, setClosingStockSourceError] = useState("");
  const [closingStockSaving, setClosingStockSaving] = useState(false);
  const materialSuggestionRefs = useRef([]);
  const quickItemPendingRowsRef = useRef(new Set());
  const eveningSkipLookupRef = useRef(false);
  const eveningCustomerSuggestionRefs = useRef([]);
  const eveningMaterialRefs = useRef({});
  const eveningMaterialSuggestionRefs = useRef([]);
  const [eveningMaterialRects, setEveningMaterialRects] = useState({});

  const refreshEntries = async () => {
    try {
      const entryList = await getInwardEntries();
      setEntries(entryList || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load inward data.");
    }
  };

  const refreshSalesEntries = async () => {
    try {
      const entryList = await getSalesEntries();
      setSalesEntries(Array.isArray(entryList) ? entryList : []);
    } catch (err) {
      console.error(err);
      alert("Failed to load sales data.");
    }
  };

  const refreshCustomers = async () => {
    try {
      const list = await getCustomers();
      setCustomers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  };

  const refreshSuppliersAndItems = async () => {
    try {
      const [supplierList, itemList] = await Promise.all([getSuppliers(), getItems()]);
      setSuppliers(supplierList || []);
      const filteredItems = Array.isArray(itemList)
        ? itemList.filter((it) => (it.itemType || "purchase").toLowerCase() === "purchase")
        : [];
      setPurchaseItems(filteredItems);
    } catch (err) {
      console.error(err);
      alert("Failed to load suppliers/items.");
    }
  };

  const refreshMyRequests = async () => {
    try {
      const list = await getMyBillActionRequests();
      setMyRequests(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    refreshSuppliersAndItems();
    refreshEntries();
    refreshSalesEntries();
    refreshCustomers();
    refreshMyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fixedSupplier) {
      setSupplierName(fixedSupplier);
    }
  }, [fixedSupplier]);

  const visibleEntries = useMemo(() => {
    return entries.filter((en) => (en.category || "General") === activeCategory);
  }, [entries, activeCategory]);

  const getSupplierPendingBalance = (entry) => {
    const supplierKey = String(entry?.supplierName || "").trim().toLowerCase();
    if (!supplierKey) {
      return getSettlementSummary(Number(entry?.total) || 0, entry?.payments).pendingAmount;
    }
    const matchingEntries = entries.filter(
      (candidate) =>
        String(candidate?.category || "General").trim() === "General" &&
        String(candidate?.supplierName || "").trim().toLowerCase() === supplierKey
    );
    const entryId = String(entry?.id || entry?.entryId || "");
    const includesEntry = entryId && matchingEntries.some((candidate) => String(candidate?.id || "") === entryId);
    const balanceEntries = includesEntry || !entry ? matchingEntries : [...matchingEntries, entry];
    return +balanceEntries
      .reduce(
        (sum, candidate) =>
          sum + getSettlementSummary(Number(candidate?.total) || 0, candidate?.payments).pendingAmount,
        0
      )
      .toFixed(2);
  };

  useEffect(() => {
    if (!showInlineClosingStockFields) return;
    setRows((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const recalculated = buildInlineClosingRows({
        rows: prev,
        visibleEntries,
        salesEntries,
        activeCategory,
        entryDate,
        editingEntry,
        supplierName,
      });
      const currentSignature = JSON.stringify(
        prev.map((row) => ({
          amount: row.amount,
          stockQty: row.stockQty,
          closingQty: row.closingQty,
          retailQty: row.retailQty,
          retailRate: row.retailRate,
          retailAmount: row.retailAmount,
        }))
      );
      const nextSignature = JSON.stringify(
        recalculated.map((row) => ({
          amount: row.amount,
          stockQty: row.stockQty,
          closingQty: row.closingQty,
          retailQty: row.retailQty,
          retailRate: row.retailRate,
          retailAmount: row.retailAmount,
        }))
      );
      return currentSignature === nextSignature ? prev : recalculated;
    });
  }, [showInlineClosingStockFields, visibleEntries, salesEntries, activeCategory, entryDate, editingEntry, supplierName]);

  useEffect(() => {
    if (lockedCategory) {
      setSavedSupplierFilter("");
      setSavedEntriesTab("inward");
      setSupplierDetailsModal({ open: false, metric: "qty" });
      setSupplierDetailsPage(1);
      setSupplierPaidForm(makeSupplierPaidForm());
      setClosingStockModalOpen(false);
      setClosingStockSourceDate("");
      setClosingStockSourceRows([]);
      setClosingStockSourceError("");
    }
  }, [lockedCategory]);

  useEffect(() => {
    if (!showEveningSalesOption) {
      setSavedEntriesTab("inward");
    }
  }, [showEveningSalesOption]);

  useLayoutEffect(() => {
    const next = {};
    rows.forEach((_, i) => {
      const el = materialRefs.current[i];
      if (el) {
        next[i] = el.getBoundingClientRect();
      }
    });
    setMaterialRects(next);
  }, [rows, purchaseItems]);

  useLayoutEffect(() => {
    if (!eveningSalesModalOpen) return;
    const next = {};
    eveningSaleRows.forEach((_, i) => {
      const el = eveningMaterialRefs.current[i];
      if (el) {
        next[i] = el.getBoundingClientRect();
      }
    });
    setEveningMaterialRects(next);
  }, [eveningSalesModalOpen, eveningSaleRows, purchaseItems]);

  useEffect(() => {
    if (activeMaterialSuggestionIndex < 0) return;
    const target = materialSuggestionRefs.current[activeMaterialSuggestionIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [activeMaterialSuggestionIndex, openMaterialIndex]);

  useEffect(() => {
    if (eveningCustomerSuggestionIndex < 0) return;
    const target = eveningCustomerSuggestionRefs.current[eveningCustomerSuggestionIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [eveningCustomerSuggestionIndex, eveningCustomerSuggestions]);

  useEffect(() => {
    if (eveningActiveMaterialSuggestionIndex < 0) return;
    const target = eveningMaterialSuggestionRefs.current[eveningActiveMaterialSuggestionIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [eveningActiveMaterialSuggestionIndex, eveningOpenMaterialIndex]);

  const totalAmount = useMemo(() => {
    if (showInlineClosingStockFields) {
      return rows.reduce((sum, row) => sum + readRetailAmount(row), 0);
    }
    return rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  }, [rows, showInlineClosingStockFields]);

  const eveningSalesTotals = useMemo(() => {
    const totalQty = eveningSaleRows.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
    const totalAmount = eveningSaleRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    return {
      totalQty: +totalQty.toFixed(2),
      totalAmount: +totalAmount.toFixed(2),
    };
  }, [eveningSaleRows]);

  const requestByEntry = useMemo(() => {
    const map = {};
    for (const req of Array.isArray(myRequests) ? myRequests : []) {
      const entryId = req?.entryId;
      const action = req?.action;
      if (!entryId || !action) continue;
      if (!map[entryId]) map[entryId] = {};
      if (!map[entryId][action]) {
        map[entryId][action] = req;
      }
    }
    return map;
  }, [myRequests]);

  const requestKey = (entryId, action) => `${String(entryId)}:${String(action)}`;

  const handleChange = (index, field, value) => {
    if (field === "material") {
      setOpenMaterialIndex(value.trim().length >= 2 ? index : null);
      setActiveMaterialSuggestionIndex(value.trim().length >= 2 ? 0 : -1);
    }
    setRows((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      const qty = Number(updated.qty) || 0;
      const rate = Number(updated.rate) || 0;
      updated.amount = +(qty * rate).toFixed(2);
      next[index] = updated;
      return showInlineClosingStockFields
        ? buildInlineClosingRows({
            rows: next,
            visibleEntries,
            salesEntries,
            activeCategory,
            entryDate,
            editingEntry,
            supplierName,
          })
        : next;
    });
  };

  const focusNextRowRate = (currentRow) => {
    const nextIndex = currentRow + 1;
    focusMaterial(nextIndex);
  };

  const focusQty = (rowIndex) => {
    const el = document.querySelector(`[data-qty-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusRate = (rowIndex) => {
    const el = document.querySelector(`[data-rate-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusClosingQty = (rowIndex) => {
    const el = document.querySelector(`[data-closing-qty-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusRetailRate = (rowIndex) => {
    const el = document.querySelector(`[data-retail-rate-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusMaterial = (rowIndex) => {
    const el = document.querySelector(`[data-material-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const handleSelectItem = (index, item) => {
    setRows((prev) => {
      const next = [...prev];
      const target = { ...next[index] };
      const rate = item?.purchasePrice ?? target.rate;
      target.material = item?.name || item?.code || "";
      target.rate = rate;
      const qty = Number(target.qty) || 0;
      target.amount = +(qty * (Number(rate) || 0)).toFixed(2);
      next[index] = target;
      return showInlineClosingStockFields
        ? buildInlineClosingRows({
            rows: next,
            visibleEntries,
            salesEntries,
            activeCategory,
            entryDate,
            editingEntry,
            supplierName,
          })
        : next;
    });
    setOpenMaterialIndex(null);
    setActiveMaterialSuggestionIndex(-1);
  };

  const ensurePurchaseItemForRow = async (index, inputValue) => {
    const materialName = String(inputValue || "").trim();
    if (!materialName || quickItemPendingRowsRef.current.has(index)) return;

    quickItemPendingRowsRef.current.add(index);
    try {
      const result = await confirmAndCreateQuickItem({
        name: materialName,
        itemType: "purchase",
        items: purchaseItems,
        confirmCreate: (message) => window.confirm(message),
        createItem: (payload) => addItem(payload),
      });
      if (result.item) {
        if (result.status === "created") {
          setPurchaseItems((current) => {
            const savedId = String(result.item?.id || "");
            const alreadyLoaded = current.some(
              (item) =>
                (savedId && String(item?.id || "") === savedId) ||
                String(item?.name || "").trim().toLocaleLowerCase() === materialName.toLocaleLowerCase()
            );
            return alreadyLoaded ? current : [result.item, ...current];
          });
        }
        handleSelectItem(index, result.item);
        return;
      }
      if (result.status === "cancelled") {
        setTimeout(() => focusMaterial(index), 0);
      }
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to add the new item to Item Master.");
      setTimeout(() => focusMaterial(index), 0);
    } finally {
      quickItemPendingRowsRef.current.delete(index);
    }
  };

  useEffect(() => {
    const phoneQuery = eveningCustomerPhone.trim().toLowerCase();
    const nameQuery = eveningCustomerName.trim().toLowerCase();

    if (eveningSkipLookupRef.current) {
      eveningSkipLookupRef.current = false;
      setEveningCustomerSuggestions([]);
      return undefined;
    }

    const query = phoneQuery || nameQuery;
    const matches = query
      ? customers
          .filter(
            (customer) =>
              String(customer?.phone || "").toLowerCase().includes(query) ||
              String(customer?.name || "").toLowerCase().includes(query)
          )
          .slice(0, 8)
      : [];
    setEveningCustomerSuggestions(matches);

    if (!phoneQuery || phoneQuery.length < 10) {
      setEveningCustomerLookup({ status: "", message: "" });
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const data = await getCustomerByPhone(eveningCustomerPhone.trim());
        setEveningCustomerName(data?.name || "");
        setEveningCustomerAddress(data?.address || "");
        setEveningCustomerLookup({ status: "found", message: "Customer found" });
      } catch (err) {
        console.error(err);
        const message = err?.message === "Not found" ? "Customer not found. Enter details manually." : "Failed to fetch customer.";
        setEveningCustomerLookup({ status: "notfound", message });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [eveningCustomerPhone, eveningCustomerName, customers]);

  useEffect(() => {
    setEveningCustomerSuggestionIndex(eveningCustomerSuggestions.length ? 0 : -1);
  }, [eveningCustomerSuggestions]);

  const applyEveningCustomerSuggestion = (customer) => {
    eveningSkipLookupRef.current = true;
    setEveningCustomerPhone(String(customer?.phone || ""));
    setEveningCustomerName(String(customer?.name || ""));
    setEveningCustomerAddress(String(customer?.address || ""));
    setEveningCustomerLookup({ status: "found", message: "Customer selected" });
    setEveningCustomerSuggestions([]);
    setEveningCustomerSuggestionIndex(-1);
  };

  const handleEveningSaleChange = (index, field, value) => {
    if (field === "material") {
      setEveningOpenMaterialIndex(value.trim().length >= 2 ? index : null);
      setEveningActiveMaterialSuggestionIndex(value.trim().length >= 2 ? 0 : -1);
    }
    setEveningSaleRows((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      const qty = Number(updated.qty) || 0;
      const rate = Number(updated.rate) || 0;
      updated.amount = +(qty * rate).toFixed(2);
      next[index] = updated;
      return next;
    });
  };

  const handleSelectEveningSaleItem = (index, item) => {
    setEveningSaleRows((prev) => {
      const next = [...prev];
      const target = { ...next[index] };
      const rate = item?.sellingPrice ?? target.rate;
      target.material = item?.name || item?.code || "";
      target.rate = rate;
      const qty = Number(target.qty) || 0;
      target.amount = +(qty * (Number(rate) || 0)).toFixed(2);
      next[index] = target;
      return next;
    });
    setEveningOpenMaterialIndex(null);
    setEveningActiveMaterialSuggestionIndex(-1);
  };

  const focusEveningMaterial = (rowIndex) => {
    const el = document.querySelector(`[data-evening-material-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusEveningQty = (rowIndex) => {
    const el = document.querySelector(`[data-evening-qty-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusEveningRate = (rowIndex) => {
    const el = document.querySelector(`[data-evening-rate-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusNextEveningMaterial = (currentRow) => {
    focusEveningMaterial(currentRow + 1);
  };

  const addEveningSaleRow = () => setEveningSaleRows((prev) => [...prev, emptyEveningSaleRow()]);

  const removeEveningSaleRow = (index) =>
    setEveningSaleRows((prev) => (prev.length > 1 ? prev.filter((_, rowIndex) => rowIndex !== index) : prev));

  const resetEveningSalesForm = (dateValue = entryDate || new Date().toISOString().slice(0, 10)) => {
    setEveningSaleDate(dateValue);
    setEveningSalePaymentType("Any");
    setEveningCustomerPhone("");
    setEveningCustomerName("");
    setEveningCustomerAddress("");
    setEveningCustomerSuggestions([]);
    setEveningCustomerSuggestionIndex(-1);
    setEveningCustomerLookup({ status: "", message: "" });
    setEveningSaleRows([emptyEveningSaleRow()]);
    setEveningOpenMaterialIndex(null);
    setEveningActiveMaterialSuggestionIndex(-1);
  };

  const openEveningSalesModal = async () => {
    if (!showEveningSalesOption) return;
    await refreshCustomers();
    setEditingEveningSaleEntry(null);
    resetEveningSalesForm(entryDate || new Date().toISOString().slice(0, 10));
    setEveningSalesModalOpen(true);
  };

  const closeEveningSalesModal = () => {
    if (eveningSalesSaving) return;
    setEveningSalesModalOpen(false);
    setEditingEveningSaleEntry(null);
    resetEveningSalesForm(entryDate || new Date().toISOString().slice(0, 10));
  };

  const startEditEveningSaleEntry = async (entry) => {
    if (!entry?.id) return;
    await refreshCustomers();
    const customer = parseCustomerDetails(entry?.customer);
    const normalizedRows = (Array.isArray(entry?.items) ? entry.items : [])
      .map((item) => {
        const qty = Number(item?.qty) || 0;
        const rate = Number(item?.rate) || 0;
        return {
          material: String(item?.material || ""),
          qty: qty ? String(qty) : "",
          rate: rate ? String(rate) : "",
          amount: +(Number(item?.amount) || qty * rate).toFixed(2),
          closingStock: Number(item?.closingStock || 0) || 0,
        };
      })
      .filter((item) => item.material || item.qty || item.rate);

    setEditingEveningSaleEntry(entry);
    setEveningSaleDate(String(entry?.saleDate || new Date().toISOString().slice(0, 10)));
    setEveningSalePaymentType(normalizeEveningSalesPaymentType(entry?.paymentType));
    setEveningCustomerPhone(customer.phone || "");
    setEveningCustomerName(customer.name || "");
    setEveningCustomerAddress(customer.address || "");
    setEveningCustomerSuggestions([]);
    setEveningCustomerSuggestionIndex(-1);
    setEveningCustomerLookup({ status: "", message: "" });
    setEveningSaleRows(normalizedRows.length ? normalizedRows : [emptyEveningSaleRow()]);
    setEveningOpenMaterialIndex(null);
    setEveningActiveMaterialSuggestionIndex(-1);
    setEveningSalesModalOpen(true);
  };

  const saveEveningSalesEntry = async () => {
    if (eveningSalesSaving) return;
    if (!window.confirm(editingEveningSaleEntry?.id ? "Update this evening sale?" : "Save this evening sale?")) return;

    const invalidRow = eveningSaleRows.find(
      (row) =>
        (row.material || "").trim() &&
        !purchaseItems.some(
          (item) =>
            String(item?.name || "").toLowerCase() === String(row.material || "").trim().toLowerCase() ||
            String(item?.code || "").toLowerCase() === String(row.material || "").trim().toLowerCase()
        )
    );
    if (invalidRow) {
      alert("Please select items only from the dropdown list.");
      return;
    }

    const items = eveningSaleRows
      .filter((row) => row.material || row.qty || row.rate)
      .map((row) => ({
        material: String(row.material || ""),
        qty: Number(row.qty) || 0,
        rate: Number(row.rate) || 0,
        amount: Number(row.amount) || 0,
        closingStock: 0,
      }));

    if (items.length === 0) {
      alert("Please enter at least one evening sale item.");
      return;
    }

    const customerText = [eveningCustomerName.trim(), eveningCustomerPhone.trim(), eveningCustomerAddress.trim()]
      .filter(Boolean)
      .join("\n");

    const payload = {
      category: "Stall",
      saleDate: eveningSaleDate,
      customer: customerText,
      source: "Evening Sales",
      items,
      paymentType: normalizeEveningSalesPaymentType(eveningSalePaymentType),
      paymentDone: normalizeEveningSalesPaymentType(eveningSalePaymentType) !== "Credit",
      total: items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
      totalQty: items.reduce((sum, item) => sum + (Number(item.qty) || 0), 0),
    };

    setEveningSalesSaving(true);
    try {
      if (editingEveningSaleEntry?.id) {
        await updateSalesEntry(editingEveningSaleEntry.id, payload);
      } else {
        await addSalesEntry({ ...payload, payments: [] });
      }
      await refreshSalesEntries();
      await refreshMyRequests();
      setSavedEntriesTab("evening-sales");
      setSavedEntriesPage(1);
      setEveningSalesModalOpen(false);
      setEditingEveningSaleEntry(null);
      resetEveningSalesForm(entryDate || new Date().toISOString().slice(0, 10));
      alert(editingEveningSaleEntry?.id ? "Evening sale updated." : "Evening sale saved.");
    } catch (err) {
      console.error(err);
      alert(err?.message || `Failed to ${editingEveningSaleEntry?.id ? "update" : "save"} evening sale.`);
    } finally {
      setEveningSalesSaving(false);
    }
  };

  const resetForm = () => {
    setRows(makeDefaultRows());
    setSupplierName("");
    setNumberOfBoxes("");
    setEntryDate(new Date().toISOString().slice(0, 10));
    setEditingEntry(null);
  };

  const startEditEntry = (entry) => {
    if (!entry) return;
    const nextEntryDate = entry.createdAt ? entry.createdAt.slice(0, 10) : new Date().toISOString().slice(0, 10);
    const normalizedRows = (entry.items || []).map((it) => {
        const qty = Number(it?.qty) || 0;
        const rate = Number(it?.rate) || 0;
        return {
          material: it?.material || "",
          qty,
          rate,
          amount: +(qty * rate).toFixed(2),
          stockQty: Number(it?.stockQty ?? it?.stock_qty ?? it?.qty ?? 0) || 0,
          closingQty: readClosingQty(it),
          closingRate: Number(it?.closingRate ?? it?.closing_rate ?? 0) || 0,
          closingAmount: Number(it?.closingAmount ?? it?.closing_amount ?? 0) || 0,
          retailQty: readRetailQty(it),
          retailRate: readRetailRate(it),
          retailAmount: readRetailAmount(it),
        };
      })
    ;
    setEditingEntry(entry);
    setSupplierName(entry.supplierName || "");
    setNumberOfBoxes(entry.numberOfBoxes ?? "");
    setEntryDate(nextEntryDate);
    setRows(
      showInlineClosingStockFields
        ? buildInlineClosingRows({
            rows: normalizedRows,
            visibleEntries,
            salesEntries,
            activeCategory,
            entryDate: nextEntryDate,
            editingEntry: entry,
            supplierName: entry.supplierName || "",
          })
        : normalizedRows
    );
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // ignore scroll errors
    }
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const handleCopyPreviousClosingStock = () => {
    if (!showInlineClosingStockFields) return;
    if (editingEntry?.id) {
      alert("Copy is available only while creating a new inward entry.");
      return;
    }

    const previousDate = addDaysYmd(entryDate || new Date().toISOString().slice(0, 10), -1);
    const previousEntries = visibleEntries.filter((entry) => toComparableYmd(entry?.createdAt) === previousDate);
    if (!previousEntries.length) {
      alert(`No ${activeCategory} inward entries found for ${formatDisplayDate(previousDate)}.`);
      return;
    }

    const copiedRows = [];
    previousEntries.forEach((entry) => {
      (Array.isArray(entry?.items) ? entry.items : []).forEach((item) => {
        const closingQty = readClosingQty(item);
        if (closingQty <= 0 || !String(item?.material || "").trim()) return;
        copiedRows.push({
          material: String(item?.material || "").trim(),
          qty: +closingQty.toFixed(3),
          rate: 0,
          amount: 0,
          stockQty: +closingQty.toFixed(3),
          closingQty: 0,
          closingRate: 0,
          closingAmount: 0,
          retailQty: +closingQty.toFixed(3),
          retailRate: 0,
          retailAmount: 0,
        });
      });
    });

    if (!copiedRows.length) {
      alert(`No closing stock is available on ${formatDisplayDate(previousDate)}.`);
      return;
    }

    const currentHasData = rows.some((row) => hasInwardDraftRowValue(row));
    if (currentHasData) {
      const shouldAppend = window.confirm(
        `Append ${copiedRows.length} copied closing stock row(s) from ${formatDisplayDate(previousDate)} to the current inward draft?`
      );
      if (!shouldAppend) return;
    }

    setRows((prev) => {
      const baseRows = currentHasData ? prev.filter((row) => hasInwardDraftRowValue(row)) : [];
      const nextRows = [...baseRows, ...copiedRows];
      return buildInlineClosingRows({
        rows: nextRows,
        visibleEntries,
        salesEntries,
        activeCategory,
        entryDate,
        editingEntry: null,
        supplierName,
      });
    });
    setOpenMaterialIndex(null);
    setActiveMaterialSuggestionIndex(-1);
  };
  
  const removeRow = (index) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const supplier = supplierName.trim();
    if (!supplier) {
      alert("Please select a supplier before saving.");
      return;
    }
    const invalidRow = rows.find(
      (r) =>
        hasInwardDraftRowValue(r) &&
        (!(r.material || "").trim() || !purchaseItems.some(
          (it) =>
            String(it.name || "").toLowerCase() === String(r.material || "").trim().toLowerCase() ||
            String(it.code || "").toLowerCase() === String(r.material || "").trim().toLowerCase()
        ))
    );
    if (invalidRow) {
      alert("Please select materials only from the dropdown list.");
      return;
    }
    let boxes = null;
    if (showNumberOfBoxes) {
      boxes = Number(numberOfBoxes);
      if (!Number.isInteger(boxes) || boxes < 1 || boxes > 2147483647) {
        alert("Enter a positive whole number of boxes for this inward entry.");
        boxesInputRef.current?.focus();
        return;
      }
    }
    const items = rows
      .filter(hasInwardDraftRowValue)
      .map((r) => ({
        date: entryDate || "",
        material: r.material || "",
        qty: Number(r.qty) || 0,
        rate: Number(r.rate) || 0,
        amount: Number(r.amount) || 0,
        stockQty: showClosingStockOption ? Number(r.stockQty) || Number(r.qty) || 0 : 0,
        closingQty: showClosingStockOption ? Number(r.closingQty) || 0 : 0,
        closingRate: showClosingStockOption ? Number(r.closingRate) || 0 : 0,
        closingAmount: showClosingStockOption ? Number(r.closingAmount) || 0 : 0,
        retailQty: showClosingStockOption ? Number(r.retailQty) || 0 : 0,
        retailRate: showClosingStockOption ? Number(r.retailRate) || 0 : 0,
        retailAmount: showClosingStockOption ? Number(r.retailAmount) || 0 : 0,
      }));
    if (items.length === 0) {
      alert("Please enter at least one line item.");
      return;
    }
    const confirmText = editingEntry ? "Update this inward entry?" : "Save this inward entry?";
    if (!window.confirm(confirmText)) return;
    const total = items.reduce((s, it) => s + it.amount, 0);
    const totalQty = items.reduce((s, it) => s + it.qty, 0);
    const createdAt = entryDate ? `${entryDate}T00:00:00.000Z` : editingEntry?.createdAt || new Date().toISOString();
    const payload = {
      supplierName: supplier,
      numberOfBoxes: boxes,
      category: activeCategory,
      createdAt,
      source: title || activeCategory,
      items,
      total,
      totalQty,
    };
    try {
      if (editingEntry?.id) {
        await updateInwardEntry(editingEntry.id, payload);
      } else {
        await addInwardEntry({ ...payload, payments: [] });
      }
      await refreshEntries();
      await refreshMyRequests();
      resetForm();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save inward entry.");
    }
  };

  const requestEntryAction = async (entry, action) => {
    const entryId = entry?.id || entry?.entryId;
    if (!entryId) return;
    if (!window.confirm(`Send ${action} request to admin for this entry?`)) return;
    const key = requestKey(entryId, action);
    setEntryRequesting((prev) => ({ ...prev, [key]: true }));
    try {
      await createBillActionRequest(entryId, action);
      await refreshMyRequests();
      alert("Request sent to admin.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to send request.");
    } finally {
      setEntryRequesting((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const printEntry = (entry) => {
    if (!entry) return;
    const balanceAmount = getSupplierPendingBalance(entry);

    const buildPrintHtml = () => {
      const logoUrl = resolvePublicAsset("Logo.png");
      const itemsRows = (entry.items || [])
        .map(
          (it, idx) => `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${escapePrintHtml(it.material || "")}</td>
            <td class="right">${Number(it.qty || 0).toFixed(2)}</td>
            <td class="right">${Number(it.rate || 0).toFixed(2)}</td>
            <td class="right">${Number(it.amount || 0).toFixed(2)}</td>
          </tr>`
        )
        .join("");
      const buildLegacyHtml = () => `<!doctype html>
      <html>
        <head>
          <title>Material Purchase Receipt</title>
          <style>
            @page { size: auto; margin: 10mm; }
            * { box-sizing: border-box; }
            body {
              font-family: Arial, Helvetica, sans-serif;
              margin: 0;
              color: #0f172a;
              background: #ffffff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .sheet {
              max-width: 860px;
              margin: 0 auto;
              padding: 8px;
            }
            .receipt-card {
              border: 1px solid #1f2937;
              border-radius: 10px;
              overflow: hidden;
            }
            .header {
              display: flex;
              align-items: center;
              gap: 14px;
              padding: 12px 14px;
              border-bottom: 1px solid #1f2937;
              background: #f8fafc;
            }
            .logo-wrap {
              width: 64px;
              height: 64px;
              border: 1px solid #cbd5e1;
              border-radius: 8px;
              background: #ffffff;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              flex: 0 0 auto;
            }
            .logo {
              width: 56px;
              height: 56px;
              object-fit: contain;
              display: block;
            }
            .header-copy {
              min-width: 0;
            }
            .title {
              margin: 0;
              font-size: 20px;
              font-weight: 800;
              color: #0f172a;
            }
            .subtitle {
              margin-top: 3px;
              font-size: 13px;
              color: #475569;
              font-weight: 600;
            }
            .meta-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 8px;
              padding: 12px 14px;
              border-bottom: 1px solid #1f2937;
              background: #ffffff;
            }
            .meta-item {
              border: 1px solid #dbe4ef;
              border-radius: 8px;
              padding: 8px 10px;
              background: #f8fafc;
            }
            .meta-label {
              display: block;
              font-size: 11px;
              color: #64748b;
              margin-bottom: 4px;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              font-weight: 700;
            }
            .meta-value {
              display: block;
              font-size: 14px;
              color: #0f172a;
              font-weight: 700;
              word-break: break-word;
            }
            .table-wrap {
              padding: 12px 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }
            th, td {
              border: 1px solid #1f2937;
              padding: 8px 9px;
              font-size: 13px;
            }
            th {
              background: #eef2f7;
              font-weight: 800;
            }
            tbody tr:nth-child(even) td {
              background: #fafcff;
            }
            .summary {
              border-top: 1px solid #1f2937;
              padding: 10px 14px 12px;
              display: flex;
              justify-content: flex-end;
              gap: 16px;
              flex-wrap: wrap;
              background: #f8fafc;
            }
            .summary-item {
              min-width: 150px;
              border: 1px solid #dbe4ef;
              border-radius: 8px;
              padding: 8px 10px;
              background: #ffffff;
            }
            .summary-label {
              font-size: 11px;
              color: #64748b;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              font-weight: 700;
            }
            .summary-value {
              margin-top: 4px;
              font-size: 16px;
              font-weight: 800;
              color: #0f172a;
            }
            .summary-value.amount {
              color: #166534;
            }
            .right { text-align: right; }
            .center { text-align: center; }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="receipt-card">
              <div class="header">
                <div class="logo-wrap">
                  <img class="logo" src="${escapePrintHtml(logoUrl)}" alt="Logo" />
                </div>
                <div class="header-copy">
                  <h1 class="title">Material Purchase Receipt</h1>
                  <div class="subtitle">ARF Seafoods</div>
                </div>
              </div>
              <div class="meta-grid">
                <div class="meta-item">
                  <span class="meta-label">Supplier</span>
                  <span class="meta-value">${escapePrintHtml(entry.supplierName || "")}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Date</span>
                  <span class="meta-value">${formatDisplayDate(entry.createdAt)}</span>
                </div>
                ${
                  String(entry?.category || "General") !== "General" && entry?.numberOfBoxes != null
                    ? `<div class="meta-item">
                        <span class="meta-label">Number of Boxes</span>
                        <span class="meta-value">${escapePrintHtml(entry.numberOfBoxes)}</span>
                      </div>`
                    : ""
                }
              </div>
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style="width:8%;">S.No</th>
                      <th>Material</th>
                      <th style="width:14%;" class="right">Qty</th>
                      <th style="width:14%;" class="right">Rate</th>
                      <th style="width:16%;" class="right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${itemsRows}
                  </tbody>
                </table>
              </div>
              <div class="summary">
                <div class="summary-item">
                  <div class="summary-label">Total Qty</div>
                  <div class="summary-value">${Number(entry.totalQty || 0).toFixed(2)}</div>
                </div>
                <div class="summary-item">
                  <div class="summary-label">Total Amount</div>
                  <div class="summary-value amount">${Number(entry.total || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>`;
      return buildPurchaseReceiptHtml(entry, balanceAmount) || buildLegacyHtml();
    };
    const printWindow = window.open("", "PRINT", "width=900,height=700");
    if (!printWindow) {
      alert("Please allow popups to print.");
      return;
    }
    const html = buildPrintHtml();
    printWindow.document.write(html);
    printWindow.document.close();
    const waitForImages = () => {
      const images = Array.from(printWindow.document.images || []);
      if (images.length === 0) return Promise.resolve();
      return Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) {
                resolve();
                return;
              }
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            })
        )
      );
    };
    Promise.race([waitForImages(), new Promise((resolve) => setTimeout(resolve, 1800))]).then(() => {
      try {
        printWindow.focus();
      } catch (e) {
        // ignore
      }
      try {
        printWindow.print();
      } finally {
        printWindow.close();
      }
    });
  };

  const handleSendWhatsApp = async (entry) => {
    if (!entry) return;
    const supplierRaw = String(entry?.supplierName || "").trim().toLowerCase();
    const supplier = suppliers.find((s) => String(s?.name || "").trim().toLowerCase() === supplierRaw);
    const phone = extractPhone(supplier?.contact || entry?.supplierName || "");
    if (!phone) {
      alert("No valid supplier contact found for this entry.");
      return;
    }

    try {
      const balanceAmount = getSupplierPendingBalance(entry);
      const logoUrl = resolvePublicAsset("Logo.png");
      const itemsRows = (entry.items || [])
        .map(
          (it, idx) => `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${escapePrintHtml(it.material || "")}</td>
            <td class="right">${Number(it.qty || 0).toFixed(2)}</td>
            <td class="right">${Number(it.rate || 0).toFixed(2)}</td>
            <td class="right">${Number(it.amount || 0).toFixed(2)}</td>
          </tr>`
        )
        .join("");
      const buildLegacyHtml = () => `<!doctype html>
      <html>
        <head>
          <title>Material Purchase Receipt</title>
          <style>
            @page { size: auto; margin: 10mm; }
            * { box-sizing: border-box; }
            body {
              font-family: Arial, Helvetica, sans-serif;
              margin: 0;
              color: #0f172a;
              background: #ffffff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .sheet {
              max-width: 860px;
              margin: 0 auto;
              padding: 8px;
            }
            .receipt-card {
              border: 1px solid #1f2937;
              border-radius: 10px;
              overflow: hidden;
            }
            .header {
              display: flex;
              align-items: center;
              gap: 14px;
              padding: 12px 14px;
              border-bottom: 1px solid #1f2937;
              background: #f8fafc;
            }
            .logo-wrap {
              width: 64px;
              height: 64px;
              border: 1px solid #cbd5e1;
              border-radius: 8px;
              background: #ffffff;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              flex: 0 0 auto;
            }
            .logo {
              width: 56px;
              height: 56px;
              object-fit: contain;
              display: block;
            }
            .header-copy {
              min-width: 0;
            }
            .title {
              margin: 0;
              font-size: 20px;
              font-weight: 800;
              color: #0f172a;
            }
            .subtitle {
              margin-top: 3px;
              font-size: 13px;
              color: #475569;
              font-weight: 600;
            }
            .meta-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 8px;
              padding: 12px 14px;
              border-bottom: 1px solid #1f2937;
              background: #ffffff;
            }
            .meta-item {
              border: 1px solid #dbe4ef;
              border-radius: 8px;
              padding: 8px 10px;
              background: #f8fafc;
            }
            .meta-label {
              display: block;
              font-size: 11px;
              color: #64748b;
              margin-bottom: 4px;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              font-weight: 700;
            }
            .meta-value {
              display: block;
              font-size: 14px;
              color: #0f172a;
              font-weight: 700;
              word-break: break-word;
            }
            .table-wrap {
              padding: 12px 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }
            th, td {
              border: 1px solid #1f2937;
              padding: 8px 9px;
              font-size: 13px;
            }
            th {
              background: #eef2f7;
              font-weight: 800;
            }
            tbody tr:nth-child(even) td {
              background: #fafcff;
            }
            .summary {
              border-top: 1px solid #1f2937;
              padding: 10px 14px 12px;
              display: flex;
              justify-content: flex-end;
              gap: 16px;
              flex-wrap: wrap;
              background: #f8fafc;
            }
            .summary-item {
              min-width: 150px;
              border: 1px solid #dbe4ef;
              border-radius: 8px;
              padding: 8px 10px;
              background: #ffffff;
            }
            .summary-label {
              font-size: 11px;
              color: #64748b;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              font-weight: 700;
            }
            .summary-value {
              margin-top: 4px;
              font-size: 16px;
              font-weight: 800;
              color: #0f172a;
            }
            .summary-value.amount {
              color: #166534;
            }
            .right { text-align: right; }
            .center { text-align: center; }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="receipt-card">
              <div class="header">
                <div class="logo-wrap">
                  <img class="logo" src="${escapePrintHtml(logoUrl)}" alt="Logo" />
                </div>
                <div class="header-copy">
                  <h1 class="title">Material Purchase Receipt</h1>
                  <div class="subtitle">ARF Seafoods</div>
                </div>
              </div>
              <div class="meta-grid">
                <div class="meta-item">
                  <span class="meta-label">Supplier</span>
                  <span class="meta-value">${escapePrintHtml(entry.supplierName || "")}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Date</span>
                  <span class="meta-value">${formatDisplayDate(entry.createdAt)}</span>
                </div>
                ${
                  String(entry?.category || "General") !== "General" && entry?.numberOfBoxes != null
                    ? `<div class="meta-item">
                        <span class="meta-label">Number of Boxes</span>
                        <span class="meta-value">${escapePrintHtml(entry.numberOfBoxes)}</span>
                      </div>`
                    : ""
                }
              </div>
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style="width:8%;">S.No</th>
                      <th>Material</th>
                      <th style="width:14%;" class="right">Qty</th>
                      <th style="width:14%;" class="right">Rate</th>
                      <th style="width:16%;" class="right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${itemsRows}
                  </tbody>
                </table>
              </div>
              <div class="summary">
                <div class="summary-item">
                  <div class="summary-label">Total Qty</div>
                  <div class="summary-value">${Number(entry.totalQty || 0).toFixed(2)}</div>
                </div>
                <div class="summary-item">
                  <div class="summary-label">Total Amount</div>
                  <div class="summary-value amount">${Number(entry.total || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>`;
      const html = buildPurchaseReceiptHtml(entry, balanceAmount) || buildLegacyHtml();

      const receiptRes = await saveReceiptHtml(html);
      let receiptLink = "";
      if (receiptRes?.url) {
        if (String(receiptRes.url).startsWith("http")) {
          receiptLink = receiptRes.url;
        } else {
          const root = getApiBaseRoot().replace(/\/$/, "");
          const raw = String(receiptRes.url);
          const tail = raw.includes("/receipts/")
            ? raw.substring(raw.indexOf("/receipts/"))
            : `/${raw.replace(/^\/+/, "")}`;
          receiptLink = `${root}${tail}`;
        }
      }

      const waText = buildInwardWhatsAppText({
        supplierName: entry?.supplierName || "",
        entryDate: formatDisplayDate(entry?.createdAt),
        numberOfBoxes:
          String(entry?.category || "General") === "General" ? null : entry?.numberOfBoxes,
        items: Array.isArray(entry?.items) ? entry.items : [],
        totalQty: Number(entry?.totalQty) || 0,
        totalAmount: Number(entry?.total) || 0,
        balanceAmount,
        receiptLink,
      });
      const waUrl = `https://wa.me/${phone}?text=${waText}`;
      const win = window.open(waUrl, "_blank");
      if (!win) {
        alert("Please allow popups to send the WhatsApp message.");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to prepare WhatsApp message.");
    }
  };

  const handleDeleteEntry = async (entry) => {
    const entryId = entry?.id || entry?.entryId;
    if (!entryId) return;
    if (!window.confirm("Delete this entry?")) return;
    setEntryDeleting((prev) => ({ ...prev, [entryId]: true }));
    try {
      await deleteInwardEntry(entryId);
      await refreshEntries();
      await refreshMyRequests();
      if (editingEntry?.id === entryId) {
        resetForm();
      }
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete entry.");
    } finally {
      setEntryDeleting((prev) => {
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
    }
  };

  const refreshSuppliers = async () => {
    try {
      const list = await getSuppliers();
      setSuppliers(list || []);
    } catch (err) {
      console.error(err);
      alert("Failed to refresh suppliers.");
    }
  };

  const handleClosingStockModalRowChange = (index, field, value) => {
    setClosingStockSourceRows((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      const stockQty = Number(updated.stockQty) || 0;
      const outwardQty = Number(updated.outwardQty) || 0;
      const closingQty = Number(updated.closingQty) || 0;
      const retailRate = Number(updated.retailRate) || 0;
      const retailQty = calculateRetailQty({ stockQty, outwardQty, closingQty, activeCategory });
      updated.retailQty = +retailQty.toFixed(3);
      updated.retailAmount = +(retailQty * retailRate).toFixed(2);
      next[index] = updated;
      return next;
    });
  };

  const openClosingStockModal = () => {
    if (!showClosingStockOption) return;
    setClosingStockModalOpen(true);
    if (closingStockAvailableDates.length === 0) {
      setClosingStockSourceError(`No ${activeCategory} inward entries found.`);
      return;
    }
    setClosingStockSourceError("");
    if (!closingStockAvailableDates.includes(closingStockSourceDate)) {
      setClosingStockSourceDate(closingStockAvailableDates.includes(entryDate) ? entryDate : closingStockAvailableDates[0]);
    }
  };

  const closeClosingStockModal = () => {
    if (closingStockSaving) return;
    setClosingStockModalOpen(false);
    setClosingStockSourceError("");
  };

  const saveClosingStockAgainstInward = async () => {
    if (!showClosingStockOption) return;
    if (!closingStockSourceDate) {
      alert("Select inward date.");
      return;
    }
    if (!closingStockSourceRows.length) {
      alert("No inward items found for selected date.");
      return;
    }

    const targetEntries = visibleEntries.filter((en) => toComparableYmd(en?.createdAt) === closingStockSourceDate);
    if (!targetEntries.length) {
      alert("No inward entries found for selected date.");
      return;
    }

    if (!window.confirm(`Save closing stock for ${formatDisplayDate(closingStockSourceDate)}?`)) {
      return;
    }

    const modalRowsByEntryAndIndex = new Map();
    for (const row of closingStockSourceRows) {
      if (!row?.entryId && row?.entryId !== 0) continue;
      const key = `${String(row.entryId)}::${Number(row.itemIndex)}`;
      modalRowsByEntryAndIndex.set(key, row);
    }

    setClosingStockSaving(true);
    try {
      for (const en of targetEntries) {
        const sourceItems = Array.isArray(en?.items) ? en.items : [];
        const items = sourceItems.map((it, idx) => {
          const modalRow = modalRowsByEntryAndIndex.get(`${String(en.id)}::${idx}`);
          const stockQty = Number(it?.stockQty ?? it?.stock_qty ?? it?.qty ?? 0) || 0;
          const closingQty = modalRow ? Number(modalRow.closingQty) || 0 : readClosingQty(it);
          const retailQty = modalRow ? Number(modalRow.retailQty) || 0 : readRetailQty(it);
          const retailRate = modalRow ? Number(modalRow.retailRate) || 0 : readRetailRate(it);
          const qty = Number(it?.qty) || 0;
          const rate = Number(it?.rate) || 0;
          return {
            date: it?.date || toComparableYmd(en?.createdAt) || "",
            material: it?.material || "",
            qty,
            rate,
            amount: +(qty * rate).toFixed(2),
            stockQty: +stockQty.toFixed(3),
            closingQty: +closingQty.toFixed(3),
            closingRate: 0,
            closingAmount: 0,
            retailQty: +retailQty.toFixed(3),
            retailRate: +retailRate.toFixed(2),
            retailAmount: +(retailQty * retailRate).toFixed(2),
          };
        });

        const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
        const totalQty = items.reduce((s, it) => s + (Number(it.qty) || 0), 0);

        await updateInwardEntry(en.id, {
          supplierName: en.supplierName || "",
          numberOfBoxes: en.numberOfBoxes,
          category: en.category || activeCategory,
          createdAt: en.createdAt || (closingStockSourceDate ? `${closingStockSourceDate}T00:00:00.000Z` : new Date().toISOString()),
          source: title || activeCategory,
          items,
          total: +total.toFixed(2),
          totalQty: +totalQty.toFixed(3),
        });
      }

      await refreshEntries();
      setClosingStockModalOpen(false);
      setClosingStockSourceError("");
      alert("Closing stock saved against inward entries.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save closing stock.");
    } finally {
      setClosingStockSaving(false);
    }
  };

  const closingStockAvailableDates = useMemo(() => {
    if (!showClosingStockOption) return [];
    return Array.from(
      new Set(
        visibleEntries
          .map((en) => toComparableYmd(en?.createdAt))
          .filter(Boolean)
      )
    ).sort((a, b) => b.localeCompare(a));
  }, [visibleEntries, showClosingStockOption]);

  useEffect(() => {
    if (!closingStockModalOpen) return;
    if (!showClosingStockOption) return;
    if (!closingStockAvailableDates.length) {
      setClosingStockSourceRows([]);
      setClosingStockSourceError(`No ${activeCategory} inward entries found.`);
      return;
    }

    const selectedDate = closingStockAvailableDates.includes(closingStockSourceDate)
      ? closingStockSourceDate
      : closingStockAvailableDates[0];
    if (selectedDate !== closingStockSourceDate) {
      setClosingStockSourceDate(selectedDate);
      return;
    }

    const nextRows = buildDistributedOutwardRows({
      entries: visibleEntries,
      salesEntries,
      activeCategory,
      selectedDate,
    });

    setClosingStockSourceRows(nextRows);
    setClosingStockSourceError(nextRows.length ? "" : "No inward items for selected date.");
  }, [
    closingStockModalOpen,
    showClosingStockOption,
    closingStockAvailableDates,
    closingStockSourceDate,
    visibleEntries,
    salesEntries,
    activeCategory,
  ]);

  const showSupplierSummaryTools = !lockedCategory && isAdmin;

  const supplierFilterOptions = useMemo(() => {
    const names = Array.from(
      new Set(
        visibleEntries
          .map((en) => String(en?.supplierName || "").trim())
          .filter(Boolean)
      )
    );
    names.sort((a, b) => a.localeCompare(b));
    return names;
  }, [visibleEntries]);

  useEffect(() => {
    if (!savedSupplierFilter) return;
    if (!supplierFilterOptions.includes(savedSupplierFilter)) {
      setSavedSupplierFilter("");
      setSupplierDetailsModal((prev) => ({ ...prev, open: false }));
    }
  }, [savedSupplierFilter, supplierFilterOptions]);

  const savedEntries = useMemo(() => {
    if (!showSupplierSummaryTools || !savedSupplierFilter) return visibleEntries;
    return visibleEntries.filter((en) => String(en?.supplierName || "").trim() === savedSupplierFilter);
  }, [visibleEntries, showSupplierSummaryTools, savedSupplierFilter]);

  const eveningSalesSavedEntries = useMemo(() => {
    if (!showEveningSalesOption) return [];
    return (Array.isArray(salesEntries) ? salesEntries : [])
      .filter(
        (entry) =>
          String(entry?.category || "").trim() === "Stall" &&
          String(entry?.source || "").trim().toLowerCase() === "evening sales"
      )
      .sort((a, b) => String(b?.saleDate || "").localeCompare(String(a?.saleDate || "")));
  }, [salesEntries, showEveningSalesOption]);

  const showingEveningSalesTab = showEveningSalesOption && savedEntriesTab === "evening-sales";
  const showSavedRetailTotals =
    (activeCategory === "Stall" || activeCategory === "Market") && !showingEveningSalesTab;
  const activeSavedEntries = showingEveningSalesTab ? eveningSalesSavedEntries : savedEntries;
  const activeSavedEntriesTotalPages = Math.max(1, Math.ceil(activeSavedEntries.length / SAVED_ENTRIES_PAGE_SIZE));

  const pagedActiveSavedEntries = useMemo(() => {
    const start = (savedEntriesPage - 1) * SAVED_ENTRIES_PAGE_SIZE;
    return activeSavedEntries.slice(start, start + SAVED_ENTRIES_PAGE_SIZE);
  }, [activeSavedEntries, savedEntriesPage]);
  const pagedSavedEntries = pagedActiveSavedEntries;

  const savedEntriesPageStart = activeSavedEntries.length === 0 ? 0 : (savedEntriesPage - 1) * SAVED_ENTRIES_PAGE_SIZE + 1;
  const savedEntriesPageEnd =
    activeSavedEntries.length === 0
      ? 0
      : Math.min((savedEntriesPage - 1) * SAVED_ENTRIES_PAGE_SIZE + pagedActiveSavedEntries.length, activeSavedEntries.length);

  useEffect(() => {
    setSavedEntriesPage(1);
  }, [savedSupplierFilter, activeCategory, savedEntriesTab]);

  useEffect(() => {
    setSavedEntriesPage((current) => Math.min(current, activeSavedEntriesTotalPages));
  }, [activeSavedEntriesTotalPages]);

  const selectedSupplierTotals = useMemo(() => {
    if (!showSupplierSummaryTools || !savedSupplierFilter) return null;
    return savedEntries.reduce(
      (acc, en) => {
        const qty =
          Number(en?.totalQty) ||
          (Array.isArray(en?.items)
            ? en.items.reduce((s, it) => s + (Number(it?.qty) || 0), 0)
            : 0);
        const settlementSummary = getSettlementSummary(Number(en?.total) || 0, en?.payments);
        const amount = settlementSummary.totalAmount;
        const paid = settlementSummary.paidAmount;
        acc.qty += qty;
        acc.amount += amount;
        acc.paid += paid;
        acc.balance += settlementSummary.pendingAmount;
        return acc;
      },
      { qty: 0, amount: 0, paid: 0, balance: 0 }
    );
  }, [savedEntries, showSupplierSummaryTools, savedSupplierFilter]);

  const selectedSupplierDateWiseRows = useMemo(() => {
    if (!showSupplierSummaryTools || !savedSupplierFilter) return [];
    const map = new Map();
    for (const en of savedEntries) {
      const dateKey = toComparableYmd(en?.createdAt) || "";
      if (!dateKey) continue;
      const qty =
        Number(en?.totalQty) ||
        (Array.isArray(en?.items) ? en.items.reduce((s, it) => s + (Number(it?.qty) || 0), 0) : 0);
      const settlementSummary = getSettlementSummary(Number(en?.total) || 0, en?.payments);
      const amount = settlementSummary.totalAmount;
      const paid = settlementSummary.paidAmount;

      if (!map.has(dateKey)) {
        map.set(dateKey, {
          date: dateKey,
          entryCount: 0,
          totalQty: 0,
          totalAmount: 0,
          totalPaid: 0,
        });
      }
      const bucket = map.get(dateKey);
      bucket.entryCount += 1;
      bucket.totalQty += qty;
      bucket.totalAmount += amount;
      bucket.totalPaid += paid;
      bucket.balance += settlementSummary.pendingAmount;
    }

    return Array.from(map.values()).sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [savedEntries, showSupplierSummaryTools, savedSupplierFilter]);

  const selectedSupplierPaidDateWiseRows = useMemo(() => {
    if (!showSupplierSummaryTools || !savedSupplierFilter) return [];
    const rows = [];
    for (const en of savedEntries) {
      const payments = Array.isArray(en?.payments) ? en.payments : [];
      for (const payment of payments) {
        if (!isPaymentSettlement(payment)) continue;
        const amount = Number(payment?.amount) || 0;
        if (amount <= 0) continue;
        rows.push({
          paymentId: String(payment?.id || ""),
          entryId: String(en?.id || ""),
          date: toComparableYmd(payment?.date) || "",
          totalPaid: amount,
          createdAt: String(payment?.created_at || payment?.createdAt || ""),
        });
      }
    }
    return rows.sort((a, b) => {
      const dateA = String(a.date || "");
      const dateB = String(b.date || "");
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      const createdA = String(a.createdAt || "");
      const createdB = String(b.createdAt || "");
      if (createdA !== createdB) return createdB.localeCompare(createdA);
      return String(b.paymentId || "").localeCompare(String(a.paymentId || ""));
    });
  }, [savedEntries, showSupplierSummaryTools, savedSupplierFilter]);

  const openSupplierDetailsModal = (metric) => {
    if (!savedSupplierFilter) return;
    setSupplierDetailsModal({ open: true, metric });
  };

  const closeSupplierDetailsModal = () => {
    setSupplierDetailsModal((prev) => ({ ...prev, open: false }));
  };

  const modalMetric = supplierDetailsModal.metric;
  const paidOnlyModal = modalMetric === "paid";
  const amountModal = modalMetric === "amount";
  const supplierDetailsRowsForModal = paidOnlyModal ? selectedSupplierPaidDateWiseRows : selectedSupplierDateWiseRows;
  const supplierDetailsColSpan = paidOnlyModal ? 3 : amountModal ? 5 : 6;
  const supplierDetailsTotalPages = Math.max(
    1,
    Math.ceil(supplierDetailsRowsForModal.length / SUPPLIER_MODAL_PAGE_SIZE)
  );

  useEffect(() => {
    setSupplierDetailsPage(1);
    setSupplierPaidForm(makeSupplierPaidForm());
    setSupplierPaidEdit({ paymentId: "", date: "", amount: "" });
    setSupplierPaidUpdating(false);
    setSupplierPaidDeleting({});
  }, [supplierDetailsModal.open, supplierDetailsModal.metric, savedSupplierFilter]);

  useEffect(() => {
    setSupplierDetailsPage((prev) => {
      if (prev < 1) return 1;
      if (prev > supplierDetailsTotalPages) return supplierDetailsTotalPages;
      return prev;
    });
  }, [supplierDetailsTotalPages]);

  const supplierDetailsPagedRows = useMemo(() => {
    const start = (supplierDetailsPage - 1) * SUPPLIER_MODAL_PAGE_SIZE;
    return supplierDetailsRowsForModal.slice(start, start + SUPPLIER_MODAL_PAGE_SIZE);
  }, [supplierDetailsRowsForModal, supplierDetailsPage]);

  const supplierDetailsPageStart =
    supplierDetailsRowsForModal.length === 0 ? 0 : (supplierDetailsPage - 1) * SUPPLIER_MODAL_PAGE_SIZE + 1;
  const supplierDetailsPageEnd =
    supplierDetailsRowsForModal.length === 0
      ? 0
      : Math.min(
          (supplierDetailsPage - 1) * SUPPLIER_MODAL_PAGE_SIZE + supplierDetailsPagedRows.length,
          supplierDetailsRowsForModal.length
        );

  const handleAddSupplierPaidFromModal = async () => {
    if (!savedSupplierFilter || modalMetric !== "paid") return;
    const amount = Number(supplierPaidForm.amount);
    const today = new Date().toISOString().slice(0, 10);
    const date = String(supplierPaidForm.date || "").trim() || today;
    if (!amount || amount <= 0) {
      alert("Enter a valid paid amount.");
      return;
    }
    if (date > today) {
      alert("Payment date cannot be in the future.");
      return;
    }

    setSupplierPaidSaving(true);
    try {
      await addSupplierPayment(savedSupplierFilter, { amount, date });
      await refreshEntries();
      setSupplierPaidForm((prev) => ({ ...prev, amount: "" }));
      setSupplierDetailsPage(1);
      alert("Paid amount saved.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save paid amount.");
    } finally {
      setSupplierPaidSaving(false);
    }
  };

  const handleStartEditSupplierPaid = (row) => {
    if (!row?.paymentId) {
      alert("Unable to edit this payment.");
      return;
    }
    setSupplierPaidEdit({
      paymentId: String(row.paymentId),
      date: String(row.date || new Date().toISOString().slice(0, 10)),
      amount: Number(row.totalPaid || 0).toFixed(2),
    });
  };

  const handleCancelEditSupplierPaid = () => {
    setSupplierPaidEdit({ paymentId: "", date: "", amount: "" });
    setSupplierPaidUpdating(false);
  };

  const handleDeleteSupplierPaid = async (row) => {
    const paymentId = String(row?.paymentId || "").trim();
    if (!paymentId) {
      alert("Unable to delete this payment.");
      return;
    }
    const amountText = Number(row?.totalPaid || 0).toFixed(2);
    const dateText = formatDisplayDate(row?.date);
    if (!window.confirm(`Delete paid entry of ${amountText} on ${dateText}?`)) {
      return;
    }

    setSupplierPaidDeleting((prev) => ({ ...prev, [paymentId]: true }));
    try {
      await deleteInwardPayment(paymentId);
      await refreshEntries();
      if (supplierPaidEdit.paymentId === paymentId) {
        setSupplierPaidEdit({ paymentId: "", date: "", amount: "" });
        setSupplierPaidUpdating(false);
      }
      alert("Paid entry deleted.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete paid entry.");
    } finally {
      setSupplierPaidDeleting((prev) => {
        const next = { ...prev };
        delete next[paymentId];
        return next;
      });
    }
  };

  const handleSaveEditSupplierPaid = async () => {
    const paymentId = String(supplierPaidEdit.paymentId || "").trim();
    if (!paymentId) return;
    const amount = Number(supplierPaidEdit.amount) || 0;
    const today = new Date().toISOString().slice(0, 10);
    const date = String(supplierPaidEdit.date || "").trim() || today;
    if (amount <= 0) {
      alert("Enter a valid paid amount.");
      return;
    }
    if (date > today) {
      alert("Payment date cannot be in the future.");
      return;
    }

    setSupplierPaidUpdating(true);
    try {
      await updateInwardPayment(paymentId, {
        amount: Number(amount.toFixed(2)),
        date,
      });
      await refreshEntries();
      setSupplierPaidEdit({ paymentId: "", date: "", amount: "" });
      alert("Paid amount updated.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to update paid amount.");
    } finally {
      setSupplierPaidUpdating(false);
    }
  };

  return (
    <div className="stock-inward-container">

      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">{title || "New Entry"}</h2>
        </div>
        {/* per-row suggestion lists are rendered inline for filtering */}
        
        <form onSubmit={handleSubmit} className="inward-form">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "12px", color: "#6b7280" }}>Supplier Name</label>
              {fixedSupplier ? (
                <input className="input-field" value={fixedSupplier} readOnly />
              ) : (
                <select value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className="select-field">
                  <option value="">Select supplier...</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "12px", color: "#6b7280" }}>Date</label>
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="input-field"
              />
            </div>
            {showNumberOfBoxes ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label htmlFor="inward-number-of-boxes" style={{ fontSize: "12px", color: "#6b7280" }}>
                  Number of Boxes *
                </label>
                <input
                  id="inward-number-of-boxes"
                  ref={boxesInputRef}
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max="2147483647"
                  step="1"
                  placeholder="Number of boxes for this entry"
                  value={numberOfBoxes}
                  onChange={(e) => setNumberOfBoxes(e.target.value)}
                  required
                  className="input-field numeric"
                />
              </div>
            ) : null}
          </div>

          <div className="table-container">
            <table className="modern-table">
              {showInlineClosingStockFields ? (
                <caption
                  style={{
                    captionSide: "top",
                    textAlign: "left",
                    padding: "0 0 12px 0",
                    fontSize: "0.84rem",
                    color: "#475569",
                    fontWeight: 600,
                  }}
                >
                  {activeCategory === "Stall"
                    ? "Retail Qty is auto-calculated as Inward Qty minus Closing Qty. Enter Closing Qty and Retail Rate in the same row."
                    : `Retail Qty is auto-calculated as Inward Qty minus ${activeCategory} sales qty minus Closing Qty. Enter Closing Qty and Retail Rate in the same row.`}
                </caption>
              ) : null}
              <colgroup>
                <col style={{width: '200px'}} />
                <col style={{width: '100px'}} />
                {!hideInlinePurchaseColumns ? <col style={{width: '120px'}} /> : null}
                {!hideInlinePurchaseColumns ? <col style={{width: '120px'}} /> : null}
                {showInlineClosingStockFields && !hideInlinePurchaseColumns ? <col style={{width: '120px'}} /> : null}
                {showInlineClosingStockFields ? <col style={{width: '120px'}} /> : null}
                {showInlineClosingStockFields ? <col style={{width: '120px'}} /> : null}
                {showInlineClosingStockFields ? <col style={{width: '120px'}} /> : null}
                {showInlineClosingStockFields ? <col style={{width: '130px'}} /> : null}
                <col style={{width: '50px'}} />
              </colgroup>
              <thead>
                <tr>
                  <th className="highlighted-col">Material</th>
                  <th>Quantity</th>
                  {!hideInlinePurchaseColumns ? <th className="highlighted-col">Rate</th> : null}
                  {!hideInlinePurchaseColumns ? <th>Amount</th> : null}
                  {showInlineClosingStockFields && !hideInlinePurchaseColumns ? <th>Stock Qty</th> : null}
                  {showInlineClosingStockFields ? <th>Closing Qty</th> : null}
                  {showInlineClosingStockFields ? <th>Retail Qty</th> : null}
                  {showInlineClosingStockFields ? <th>Retail Rate</th> : null}
                  {showInlineClosingStockFields ? <th>Retail Amount</th> : null}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const q = (row.material || "").toLowerCase();
                  const hasQuery = q.length >= 2;
                  const materialOptions = hasQuery
                    ? purchaseItems
                        .filter(
                          (it) =>
                            ((it.name || "").toLowerCase().includes(q)) ||
                            ((it.code || "").toLowerCase().includes(q))
                        )
                        .slice(0, 20)
                    : [];
                  return (
                    <tr key={i} className="table-row">
                    <td>
                      <div style={{ position: "relative", zIndex: 10 }} ref={(el)=> { materialRefs.current[i] = el; }}>
                        <input
                          type="text"
                          placeholder="Material"
                          value={row.material}
                          required={hasInwardDraftRowValue(row)}
                          onChange={(e) => handleChange(i, "material", e.target.value)}
                          className="input-field"
                          autoComplete="off"
                          data-material-row={i}
                          onKeyDown={(e) => {
                            if (e.key === "ArrowDown" && materialOptions.length > 0) {
                              e.preventDefault();
                              setOpenMaterialIndex(i);
                              setActiveMaterialSuggestionIndex((prev) =>
                                openMaterialIndex === i ? Math.min(prev + 1, materialOptions.length - 1) : 0
                              );
                              return;
                            }
                            if (e.key === "ArrowUp" && materialOptions.length > 0) {
                              e.preventDefault();
                              setOpenMaterialIndex(i);
                              setActiveMaterialSuggestionIndex((prev) =>
                                openMaterialIndex === i ? Math.max(prev - 1, 0) : 0
                              );
                              return;
                            }
                            if (e.key === "Escape") {
                              setOpenMaterialIndex(null);
                              setActiveMaterialSuggestionIndex(-1);
                              return;
                            }
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (openMaterialIndex === i && materialOptions.length > 0) {
                                const picked =
                                  materialOptions[
                                    activeMaterialSuggestionIndex >= 0 ? activeMaterialSuggestionIndex : 0
                                  ];
                                if (picked) {
                                  handleSelectItem(i, picked);
                                  setTimeout(() => focusQty(i), 0);
                                }
                                return;
                              }
                              focusQty(i);
                            }
                          }}
                          onBlur={(event) => {
                            const materialName = event.currentTarget.value;
                            setTimeout(() => {
                              setOpenMaterialIndex(null);
                              setActiveMaterialSuggestionIndex(-1);
                            }, 100);
                            void ensurePurchaseItemForRow(i, materialName);
                          }}
                        />
                        {openMaterialIndex === i && hasQuery && materialOptions.length > 0 && materialRects[i] ? (
                          <PortalDropdown
                            className="autocomplete-dropdown"
                            style={{
                              position: "absolute",
                              top: materialRects[i].bottom + window.scrollY + 6,
                              left: materialRects[i].left + window.scrollX,
                              width: materialRects[i].width,
                            }}
                          >
                            {materialOptions.map((item, optionIndex) => (
                              <button
                                key={item.id || item.code || item.name}
                                type="button"
                                ref={(el) => {
                                  materialSuggestionRefs.current[optionIndex] = el;
                                }}
                                style={{
                                  width: "100%",
                                  textAlign: "left",
                                  padding: "8px 10px",
                                  border: "none",
                                  background: optionIndex === activeMaterialSuggestionIndex ? "#eff6ff" : "transparent",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={() => setActiveMaterialSuggestionIndex(optionIndex)}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleSelectItem(i, item);
                                }}
                              >
                                <div style={{ fontWeight: 600 }}>{item.name || item.code}</div>
                                <div style={{ fontSize: "0.8rem", color: "#475569" }}>
                                  {item.code ? `${item.code}` : ""} {item.purchasePrice ? `| ₹${Number(item.purchasePrice).toFixed(2)}` : ""}
                                </div>
                              </button>
                            ))}
                          </PortalDropdown>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        placeholder="0"
                        value={row.qty}
                        onChange={(e) => handleChange(i, "qty", e.target.value)}
                        className="input-field numeric no-spinner"
                        data-qty-row={i}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (hideInlinePurchaseColumns) {
                              focusClosingQty(i);
                            } else {
                              focusRate(i);
                            }
                          }
                        }}
                      />
                    </td>
                    {!hideInlinePurchaseColumns ? (
                      <td>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="any"
                          placeholder="0.00"
                          value={row.rate}
                          onChange={(e) => handleChange(i, "rate", e.target.value)}
                          className="input-field numeric"
                          data-rate-row={i}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (showInlineClosingStockFields) {
                                focusClosingQty(i);
                              } else {
                                focusNextRowRate(i);
                              }
                            }
                          }}
                        />
                      </td>
                    ) : null}
                    {!hideInlinePurchaseColumns ? (
                      <td>
                        <input 
                          type="number" 
                          value={row.amount} 
                          readOnly 
                          className="input-field calculated"
                        />
                      </td>
                    ) : null}
                    {showInlineClosingStockFields && !hideInlinePurchaseColumns ? (
                      <td>
                        <input
                          type="number"
                          value={Number(row.stockQty || 0).toFixed(3)}
                          readOnly
                          className="input-field calculated"
                        />
                      </td>
                    ) : null}
                    {showInlineClosingStockFields ? (
                      <td>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="any"
                          placeholder="0"
                          value={row.closingQty}
                          onChange={(e) => handleChange(i, "closingQty", e.target.value)}
                          className="input-field numeric"
                          data-closing-qty-row={i}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              focusRetailRate(i);
                            }
                          }}
                        />
                      </td>
                    ) : null}
                    {showInlineClosingStockFields ? (
                      <td>
                        <input
                          type="number"
                          value={Number(row.retailQty || 0).toFixed(3)}
                          readOnly
                          className="input-field calculated"
                        />
                      </td>
                    ) : null}
                    {showInlineClosingStockFields ? (
                      <td>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="any"
                          placeholder="0.00"
                          value={row.retailRate}
                          onChange={(e) => handleChange(i, "retailRate", e.target.value)}
                          className="input-field numeric"
                          data-retail-rate-row={i}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              focusNextRowRate(i);
                            }
                          }}
                        />
                      </td>
                    ) : null}
                    {showInlineClosingStockFields ? (
                      <td>
                        <input
                          type="number"
                          value={Number(row.retailAmount || 0).toFixed(2)}
                          readOnly
                          className="input-field calculated"
                        />
                      </td>
                    ) : null}
                    <td>
                      <button 
                        type="button" 
                        onClick={() => removeRow(i)} 
                        className="btn-remove"
                        title="Remove row"
                      >
                        X
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {hideInlinePurchaseColumns ? (
                  <tr className="total-row">
                    <td colSpan={4}></td>
                    <td className="total-label">Total</td>
                    <td>
                      <input
                        type="number"
                        value={totalAmount.toFixed(2)}
                        readOnly
                        className="input-field total-amount"
                      />
                    </td>
                    <td></td>
                  </tr>
                ) : (
                  <tr className="total-row">
                    <td colSpan={2}></td>
                    <td className="total-label">Total</td>
                    <td>
                      <input 
                        type="number" 
                        value={totalAmount.toFixed(2)} 
                        readOnly 
                        className="input-field total-amount"
                      />
                    </td>
                    <td colSpan={showInlineClosingStockFields ? 6 : 1}></td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              onClick={addRow} 
              disabled={!supplierName}
              className="btn-secondary"
              title={!supplierName ? "Select supplier first" : undefined}
            >
              <span className="btn-icon">+</span>
              Add Row
            </button>
            {showInlineClosingStockFields ? (
              <button
                type="button"
                onClick={handleCopyPreviousClosingStock}
                disabled={!supplierName || !!editingEntry}
                className="btn-secondary"
                title={editingEntry ? "Copy is available only for new inward entries" : undefined}
              >
                Copy
              </button>
            ) : null}
            {showClosingStockOption && !showInlineClosingStockFields ? (
              <button
                type="button"
                onClick={openClosingStockModal}
                className="btn-secondary"
              >
                Closing Stock
              </button>
            ) : null}
            {showEveningSalesOption ? (
              <button
                type="button"
                onClick={openEveningSalesModal}
                className="btn-secondary"
              >
                Evening Sales
              </button>
            ) : null}
            <button 
              type="button" 
              onClick={refreshSuppliers}
              className="btn-secondary"
            >
              <span className="btn-icon">↻</span>
              Refresh Suppliers
            </button>
            <button type="submit" className="btn-primary">
              <span className="btn-icon">💾</span>
              {editingEntry ? "Update Entry" : "Save Entry"}
            </button>
          </div>
        </form>
      </div>

      {closingStockModalOpen && !showInlineClosingStockFields ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={closeClosingStockModal}>
          <div
            className="modal-card"
            style={{ width: "min(920px, 100%)", maxHeight: "85vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">{activeCategory} Closing Stock</h3>
              <button type="button" className="btn-remove" onClick={closeClosingStockModal} style={{ width: 28, height: 28, padding: 0 }}>
                X
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 320px) auto", gap: 10, alignItems: "end", marginBottom: 12 }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Inward Date</label>
                  <select
                    className="select-field"
                    value={closingStockSourceDate}
                    onChange={(e) => {
                      setClosingStockSourceDate(e.target.value);
                      setClosingStockSourceError("");
                    }}
                    disabled={closingStockAvailableDates.length === 0}
                  >
                    {closingStockAvailableDates.length === 0 ? <option value="">No dates found</option> : null}
                    {closingStockAvailableDates.map((d) => (
                      <option key={d} value={d}>
                        {formatDisplayDate(d)}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={async () => {
                      await refreshEntries();
                      await refreshSalesEntries();
                    }}
                  >
                    Refresh Data
                  </button>
                </div>
              </div>

              {closingStockSourceError ? (
                <div style={{ marginBottom: 10, color: "#b91c1c", fontSize: "0.85rem" }}>{closingStockSourceError}</div>
              ) : null}

              <div style={{ marginBottom: 10, fontSize: "0.85rem", color: "#475569" }}>
                {activeCategory === "Stall"
                  ? "Retail Qty is auto-calculated as Inward Qty minus Closing Qty. Only Closing Qty and Retail Rate need to be entered."
                  : `Retail Qty is auto-calculated as Inward Qty minus ${activeCategory} sales qty minus Closing Qty. Only Closing Qty and Retail Rate need to be entered.`}
              </div>

              <div className="entries-table-container">
                <table className="entries-table" style={{ minWidth: 1040 }}>
                  <thead>
                    <tr>
                      <th>Supplier</th>
                      <th>Material</th>
                      <th className="text-right">Stock Qty</th>
                      <th className="text-right">Closing Qty</th>
                      <th className="text-right">Retail Qty</th>
                      <th className="text-right">Retail Rate</th>
                      <th className="text-right">Retail Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closingStockSourceRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="empty-state" style={{ padding: 12 }}>
                          {closingStockSourceDate ? "No inward items for selected date" : "Select an inward date"}
                        </td>
                      </tr>
                    ) : (
                      closingStockSourceRows.map((row, idx) => (
                        <tr key={row.key || `${row.material}-${idx}`}>
                          <td>{row.supplierName || "-"}</td>
                          <td>{row.material}</td>
                          <td className="text-right">{Number(row.stockQty || 0).toFixed(3)}</td>
                          <td>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="any"
                              placeholder="0"
                              value={row.closingQty}
                              onChange={(e) => handleClosingStockModalRowChange(idx, "closingQty", e.target.value)}
                              className="input-field numeric"
                            />
                          </td>
                          <td className="text-right">{Number(row.retailQty || 0).toFixed(3)}</td>
                          <td>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="any"
                              placeholder="0.00"
                              value={row.retailRate}
                              onChange={(e) => handleClosingStockModalRowChange(idx, "retailRate", e.target.value)}
                              className="input-field numeric"
                            />
                          </td>
                          <td className="text-right">{Number(row.retailAmount || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeClosingStockModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={saveClosingStockAgainstInward}
                disabled={closingStockSourceRows.length === 0 || closingStockSaving}
              >
                {closingStockSaving ? "Saving..." : "Save Closing Stock"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {eveningSalesModalOpen ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={closeEveningSalesModal}>
          <div
            className="modal-card"
            style={{ width: "min(980px, 100%)", maxHeight: "85vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">
                {editingEveningSaleEntry?.id ? "Edit Stall Evening Sales" : "Stall Evening Sales"}
              </h3>
              <button
                type="button"
                className="btn-remove"
                onClick={closeEveningSalesModal}
                disabled={eveningSalesSaving}
                style={{ width: 28, height: 28, padding: 0 }}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Sale Date</label>
                  <input
                    className="input-field"
                    type="date"
                    value={eveningSaleDate}
                    onChange={(e) => setEveningSaleDate(e.target.value)}
                    disabled={eveningSalesSaving}
                  />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Payment Type</label>
                  <select
                    className="select-field"
                    value={eveningSalePaymentType}
                    onChange={(e) => setEveningSalePaymentType(normalizeEveningSalesPaymentType(e.target.value))}
                    disabled={eveningSalesSaving}
                  >
                    {EVENING_SALES_PAYMENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Customer Phone</label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="input-field"
                      type="text"
                      inputMode="numeric"
                      value={eveningCustomerPhone}
                      onChange={(e) => setEveningCustomerPhone(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown" && eveningCustomerSuggestions.length > 0) {
                          e.preventDefault();
                          setEveningCustomerSuggestionIndex((prev) => Math.min(prev + 1, eveningCustomerSuggestions.length - 1));
                          return;
                        }
                        if (e.key === "ArrowUp" && eveningCustomerSuggestions.length > 0) {
                          e.preventDefault();
                          setEveningCustomerSuggestionIndex((prev) => Math.max(prev - 1, 0));
                          return;
                        }
                        if (e.key === "Escape") {
                          setEveningCustomerSuggestions([]);
                          setEveningCustomerSuggestionIndex(-1);
                          return;
                        }
                        if (e.key === "Enter" && eveningCustomerSuggestions.length > 0) {
                          e.preventDefault();
                          applyEveningCustomerSuggestion(
                            eveningCustomerSuggestions[
                              eveningCustomerSuggestionIndex >= 0 ? eveningCustomerSuggestionIndex : 0
                            ]
                          );
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setEveningCustomerSuggestions([]);
                          setEveningCustomerSuggestionIndex(-1);
                        }, 120);
                      }}
                      placeholder="Phone number"
                      autoComplete="off"
                      disabled={eveningSalesSaving}
                    />
                    {eveningCustomerSuggestions.length > 0 ? (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 4px)",
                          left: 0,
                          right: 0,
                          borderRadius: 8,
                          border: "1px solid #cbd5e1",
                          background: "#fff",
                          maxHeight: 220,
                          overflowY: "auto",
                          boxShadow: "0 12px 26px rgba(15, 23, 42, 0.14)",
                          zIndex: 40,
                        }}
                      >
                        {eveningCustomerSuggestions.map((customer, index) => (
                          <button
                            key={customer.id || `${customer.name}-${customer.phone}`}
                            type="button"
                            ref={(el) => {
                              eveningCustomerSuggestionRefs.current[index] = el;
                            }}
                            style={{
                              width: "100%",
                              border: "none",
                              background: index === eveningCustomerSuggestionIndex ? "#eff6ff" : "transparent",
                              cursor: "pointer",
                              textAlign: "left",
                              padding: "8px 10px",
                            }}
                            onMouseEnter={() => setEveningCustomerSuggestionIndex(index)}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              applyEveningCustomerSuggestion(customer);
                            }}
                          >
                            <div style={{ fontWeight: 700 }}>{customer.name || "Unnamed"}</div>
                            <div style={{ fontSize: "0.8rem", color: "#475569" }}>{customer.phone || ""}</div>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  {eveningCustomerLookup.message ? (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: "0.75rem",
                        color: eveningCustomerLookup.status === "found" ? "#16a34a" : eveningCustomerLookup.status === "notfound" ? "#b91c1c" : "#64748b",
                      }}
                    >
                      {eveningCustomerLookup.message}
                    </div>
                  ) : null}
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Customer Name</label>
                  <input
                    className="input-field"
                    type="text"
                    value={eveningCustomerName}
                    onChange={(e) => setEveningCustomerName(e.target.value)}
                    placeholder="Name"
                    disabled={eveningSalesSaving}
                  />
                </div>
                <div className="field" style={{ marginBottom: 0, gridColumn: "1 / -1" }}>
                  <label>Address</label>
                  <textarea
                    className="input-field"
                    rows={2}
                    value={eveningCustomerAddress}
                    onChange={(e) => setEveningCustomerAddress(e.target.value)}
                    placeholder="Address"
                    disabled={eveningSalesSaving}
                  />
                </div>
              </div>

              <div className="table-container">
                <table className="modern-table">
                  <colgroup>
                    <col style={{ width: "44%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "6%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {eveningSaleRows.map((row, index) => {
                      const query = String(row.material || "").toLowerCase();
                      const hasQuery = query.length >= 2;
                      const materialOptions = hasQuery
                        ? purchaseItems
                            .filter(
                              (item) =>
                                String(item?.name || "").toLowerCase().includes(query) ||
                                String(item?.code || "").toLowerCase().includes(query)
                            )
                            .slice(0, 20)
                        : [];

                      return (
                        <tr key={`evening-row-${index}`}>
                          <td>
                            <div style={{ position: "relative", zIndex: 10 }} ref={(el) => { eveningMaterialRefs.current[index] = el; }}>
                              <input
                                className="input-field"
                                type="text"
                                value={row.material}
                                onChange={(e) => handleEveningSaleChange(index, "material", e.target.value)}
                                placeholder="Item / Fish"
                                autoComplete="off"
                                data-evening-material-row={index}
                                disabled={eveningSalesSaving}
                                onKeyDown={(e) => {
                                  if (e.key === "ArrowDown" && materialOptions.length > 0) {
                                    e.preventDefault();
                                    setEveningOpenMaterialIndex(index);
                                    setEveningActiveMaterialSuggestionIndex((prev) =>
                                      eveningOpenMaterialIndex === index ? Math.min(prev + 1, materialOptions.length - 1) : 0
                                    );
                                    return;
                                  }
                                  if (e.key === "ArrowUp" && materialOptions.length > 0) {
                                    e.preventDefault();
                                    setEveningOpenMaterialIndex(index);
                                    setEveningActiveMaterialSuggestionIndex((prev) =>
                                      eveningOpenMaterialIndex === index ? Math.max(prev - 1, 0) : 0
                                    );
                                    return;
                                  }
                                  if (e.key === "Escape") {
                                    setEveningOpenMaterialIndex(null);
                                    setEveningActiveMaterialSuggestionIndex(-1);
                                    return;
                                  }
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (eveningOpenMaterialIndex === index && materialOptions.length > 0) {
                                      const picked =
                                        materialOptions[
                                          eveningActiveMaterialSuggestionIndex >= 0 ? eveningActiveMaterialSuggestionIndex : 0
                                        ];
                                      if (picked) {
                                        handleSelectEveningSaleItem(index, picked);
                                        setTimeout(() => focusEveningQty(index), 0);
                                      }
                                      return;
                                    }
                                    focusEveningQty(index);
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(() => {
                                    setEveningOpenMaterialIndex(null);
                                    setEveningActiveMaterialSuggestionIndex(-1);
                                  }, 120);
                                }}
                              />
                              {eveningOpenMaterialIndex === index && hasQuery && materialOptions.length > 0 && eveningMaterialRects[index] ? (
                                <PortalDropdown
                                  className="autocomplete-dropdown"
                                  style={{
                                    position: "absolute",
                                    top: eveningMaterialRects[index].bottom + window.scrollY + 6,
                                    left: eveningMaterialRects[index].left + window.scrollX,
                                    width: eveningMaterialRects[index].width,
                                  }}
                                >
                                  {materialOptions.map((item, optionIndex) => (
                                    <button
                                      key={item.id || item.code || item.name}
                                      type="button"
                                      ref={(el) => {
                                        eveningMaterialSuggestionRefs.current[optionIndex] = el;
                                      }}
                                      style={{
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "8px 10px",
                                        border: "none",
                                        background: optionIndex === eveningActiveMaterialSuggestionIndex ? "#eff6ff" : "transparent",
                                        cursor: "pointer",
                                      }}
                                      onMouseEnter={() => setEveningActiveMaterialSuggestionIndex(optionIndex)}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelectEveningSaleItem(index, item);
                                      }}
                                    >
                                      <div style={{ fontWeight: 600 }}>{item.name || item.code}</div>
                                      <div style={{ fontSize: "0.8rem", color: "#475569" }}>
                                        {item.code ? `${item.code}` : ""} {item.sellingPrice ? `| Rs ${Number(item.sellingPrice).toFixed(2)}` : ""}
                                      </div>
                                    </button>
                                  ))}
                                </PortalDropdown>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <input
                              className="input-field numeric"
                              type="number"
                              inputMode="decimal"
                              step="any"
                              value={row.qty}
                              onChange={(e) => handleEveningSaleChange(index, "qty", e.target.value)}
                              data-evening-qty-row={index}
                              disabled={eveningSalesSaving}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  focusEveningRate(index);
                                }
                              }}
                            />
                          </td>
                          <td>
                            <input
                              className="input-field numeric"
                              type="number"
                              inputMode="decimal"
                              step="any"
                              value={row.rate}
                              onChange={(e) => handleEveningSaleChange(index, "rate", e.target.value)}
                              data-evening-rate-row={index}
                              disabled={eveningSalesSaving}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  focusNextEveningMaterial(index);
                                }
                              }}
                            />
                          </td>
                          <td>
                            <input className="input-field calculated" type="number" value={row.amount} readOnly />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn-remove"
                              onClick={() => removeEveningSaleRow(index)}
                              disabled={eveningSalesSaving}
                              style={{ width: 32, height: 32, padding: 0 }}
                            >
                              X
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" className="btn-secondary" onClick={addEveningSaleRow} disabled={eveningSalesSaving}>
                    + Add Row
                  </button>
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontWeight: 700 }}>
                  <span>Total Qty: {eveningSalesTotals.totalQty.toFixed(2)}</span>
                  <span>Total Amount: {eveningSalesTotals.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeEveningSalesModal} disabled={eveningSalesSaving}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={saveEveningSalesEntry} disabled={eveningSalesSaving}>
                {eveningSalesSaving
                  ? "Saving..."
                  : editingEveningSaleEntry?.id
                    ? "Update Evening Sales"
                    : "Save Evening Sales"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="entries-card">
        <div className="card-header">
          <h2 className="card-title">Saved Entries</h2>
          <span className="entry-count">
            {activeSavedEntries.length}
            {showSupplierSummaryTools && !showingEveningSalesTab && savedSupplierFilter ? ` / ${visibleEntries.length}` : ""} entries
          </span>
        </div>
        {showEveningSalesOption ? (
          <div className="saved-entry-tabs">
            <button
              type="button"
              className={`saved-entry-tab ${!showingEveningSalesTab ? "active" : ""}`}
              onClick={() => setSavedEntriesTab("inward")}
            >
              Inward Entries
            </button>
            <button
              type="button"
              className={`saved-entry-tab ${showingEveningSalesTab ? "active" : ""}`}
              onClick={() => setSavedEntriesTab("evening-sales")}
            >
              Evening Sales
            </button>
          </div>
        ) : null}
        {showSupplierSummaryTools && !showingEveningSalesTab ? (
          <div style={{ padding: "12px 16px 0" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ minWidth: 240, flex: "1 1 260px" }}>
                <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: 6 }}>
                  Supplier Filter
                </label>
                <select
                  className="select-field"
                  value={savedSupplierFilter}
                  onChange={(e) => setSavedSupplierFilter(e.target.value)}
                >
                  <option value="">All Suppliers</option>
                  {supplierFilterOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              {savedSupplierFilter && selectedSupplierTotals ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "stretch", flex: "2 1 420px" }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => openSupplierDetailsModal("qty")}
                    style={{ padding: "8px 10px", minWidth: 120, textAlign: "left" }}
                    title="Open date-wise quantity details"
                  >
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Qty</div>
                    <div style={{ fontWeight: 800 }}>{Number(selectedSupplierTotals.qty || 0).toFixed(2)}</div>
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => openSupplierDetailsModal("amount")}
                    style={{ padding: "8px 10px", minWidth: 140, textAlign: "left" }}
                    title="Open date-wise amount details"
                  >
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Amount</div>
                    <div style={{ fontWeight: 800 }}>{Number(selectedSupplierTotals.amount || 0).toFixed(2)}</div>
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => openSupplierDetailsModal("paid")}
                    style={{ padding: "8px 10px", minWidth: 140, textAlign: "left" }}
                    title="Open date-wise paid details"
                  >
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Paid</div>
                    <div style={{ fontWeight: 800, color: "#16a34a" }}>
                      {Number(selectedSupplierTotals.paid || 0).toFixed(2)}
                    </div>
                  </button>
                  <div
                    style={{
                      padding: "8px 10px",
                      minWidth: 140,
                      textAlign: "left",
                      borderRadius: 10,
                      border: "1px solid #cbd5e1",
                      background: "#f8fafc",
                      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                    }}
                    title="Remaining balance after payments and adjustments"
                  >
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Balance</div>
                    <div
                      style={{
                        fontWeight: 800,
                        color: Number(selectedSupplierTotals.balance || 0) > 0 ? "#b91c1c" : "#16a34a",
                      }}
                    >
                      {Number(selectedSupplierTotals.balance || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        
        {activeSavedEntries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <p>
              {showingEveningSalesTab
                ? "No evening sales yet"
                : savedSupplierFilter
                  ? "No entries for selected supplier"
                  : "No entries yet"}
            </p>
            <span>
              {showingEveningSalesTab
                ? "Evening sales saved from this screen will appear here"
                : savedSupplierFilter
                  ? "Try another supplier or clear the filter"
                  : "Save your first entry to see it here"}
            </span>
          </div>
        ) : (
          <>
            {showingEveningSalesTab ? (
              <div className="entries-table-container">
                <table className="entries-table">
                  <colgroup>
                    <col style={{ width: "50px" }} />
                    <col style={{ width: "140px" }} />
                    <col style={{ width: "280px" }} />
                    <col style={{ width: "140px" }} />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "140px" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Payment Type</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedActiveSavedEntries.map((en) => {
                      const entryKey = `evening-sale:${en.id || en.entryId}`;
                      const baseEntryId = en.id || en.entryId;
                      const isOpen = !!expanded[entryKey];
                      const customer = parseCustomerDetails(en?.customer);
                      const totalQty =
                        Number(en?.totalQty) ||
                        (Array.isArray(en?.items) ? en.items.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0) : 0);
                      const editReq = requestByEntry[baseEntryId]?.edit;
                      const editStatus = String(editReq?.status || "");
                      const editKey = requestKey(baseEntryId, "edit");
                      const editBusy = !!entryRequesting[editKey];

                      return (
                        <React.Fragment key={entryKey}>
                          <tr className={`entry-row ${isOpen ? "expanded" : ""}`}>
                            <td>
                              <button
                                type="button"
                                className="expand-btn"
                                onClick={() => setExpanded((ex) => ({ ...ex, [entryKey]: !isOpen }))}
                              >
                                <span className={`expand-icon ${isOpen ? "rotated" : ""}`}>›</span>
                              </button>
                            </td>
                            <td className="entry-date">{formatDisplayDate(en?.saleDate)}</td>
                            <td className="supplier-name">{customer.name || customer.phone || "-"}</td>
                            <td className="entry-date">{normalizeEveningSalesPaymentType(en?.paymentType)}</td>
                            <td className="text-right quantity">{Number(totalQty || 0).toFixed(2)}</td>
                            <td className="text-right amount">{Number(en?.total || 0).toFixed(2)}</td>
                            <td className="text-right">
                              {isAdmin || editStatus === "approved" ? (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => startEditEveningSaleEntry(en)}
                                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                >
                                  Edit
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => requestEntryAction(en, "edit")}
                                  disabled={editBusy || editStatus === "pending"}
                                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                  title={editStatus === "pending" ? "Edit request pending" : "Request edit"}
                                >
                                  {editStatus === "pending" ? "Edit Pending" : "Request Edit"}
                                </button>
                              )}
                            </td>
                          </tr>
                          {isOpen ? (
                            <tr className="expanded-content">
                              <td></td>
                              <td colSpan={6}>
                                <div className="expansion-panel">
                                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap", marginBottom: 10 }}>
                                    {isAdmin || editStatus === "approved" ? (
                                      <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => startEditEveningSaleEntry(en)}
                                        style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                      >
                                        Edit
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => requestEntryAction(en, "edit")}
                                        disabled={editBusy || editStatus === "pending"}
                                        style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                        title={editStatus === "pending" ? "Edit request pending" : "Request edit"}
                                      >
                                        {editStatus === "pending" ? "Edit Pending" : "Request Edit"}
                                      </button>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                      gap: 10,
                                      marginBottom: 12,
                                    }}
                                  >
                                    <div className="meta-item">
                                      <span className="meta-label">Customer</span>
                                      <span className="meta-value">{customer.name || "-"}</span>
                                    </div>
                                    <div className="meta-item">
                                      <span className="meta-label">Phone</span>
                                      <span className="meta-value">{customer.phone || "-"}</span>
                                    </div>
                                    <div className="meta-item">
                                      <span className="meta-label">Address</span>
                                      <span className="meta-value">{customer.address || "-"}</span>
                                    </div>
                                    <div className="meta-item">
                                      <span className="meta-label">Payment Type</span>
                                      <span className="meta-value">{normalizeEveningSalesPaymentType(en?.paymentType)}</span>
                                    </div>
                                  </div>
                                  <div className="items-section">
                                    <h4 className="section-title">Items</h4>
                                    <table className="items-table">
                                      <colgroup>
                                        <col style={{ width: "220px" }} />
                                        <col style={{ width: "120px" }} />
                                        <col style={{ width: "120px" }} />
                                        <col style={{ width: "140px" }} />
                                      </colgroup>
                                      <thead>
                                        <tr>
                                          <th>Material</th>
                                          <th className="text-right">Qty</th>
                                          <th className="text-right">Rate</th>
                                          <th className="text-right">Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(Array.isArray(en?.items) ? en.items : []).map((item, index) => (
                                          <tr key={`${entryKey}-item-${index}`}>
                                            <td>{item?.material || "-"}</td>
                                            <td className="text-right">{Number(item?.qty || 0).toFixed(2)}</td>
                                            <td className="text-right">{Number(item?.rate || 0).toFixed(2)}</td>
                                            <td className="text-right">{Number(item?.amount || 0).toFixed(2)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
            <div className="entries-table-container">
              <table className="entries-table">
              <colgroup>
                <col style={{width: '50px'}} />
                <col style={{width: '260px'}} />
                <col style={{width: '140px'}} />
                <col style={{width: '140px'}} />
                <col style={{width: '100px'}} />
                <col style={{width: '120px'}} />
                {showSavedRetailTotals ? <col style={{width: '120px'}} /> : null}
                {showSavedRetailTotals ? <col style={{width: '140px'}} /> : null}
                {!lockedCategory ? <col style={{width: '180px'}} /> : null}
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>Supplier</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Amount</th>
                  {showSavedRetailTotals ? <th className="text-right">Retail Qty</th> : null}
                  {showSavedRetailTotals ? <th className="text-right">Retail Amount</th> : null}
                  {!lockedCategory ? <th className="text-right">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {pagedSavedEntries.map((en) => {
                  const entryKey = en.id || en.entryId;
                  const isOpen = !!expanded[entryKey];
                  const entryItems = Array.isArray(en?.items) ? en.items : [];
                  const totalQty = entryItems.reduce((s, it) => s + (Number(it.qty) || 0), 0);
                  const totalRetailQty = showSavedRetailTotals
                    ? entryItems.reduce((s, it) => s + readRetailQty(it), 0)
                    : 0;
                  const totalRetailAmount = showSavedRetailTotals
                    ? entryItems.reduce((s, it) => s + readRetailAmount(it), 0)
                    : 0;
                  const editReq = requestByEntry[entryKey]?.edit;
                  const delReq = requestByEntry[entryKey]?.delete;
                  const editStatus = String(editReq?.status || "");
                  const delStatus = String(delReq?.status || "");
                  const editKey = requestKey(en.id, "edit");
                  const delKey = requestKey(en.id, "delete");
                  const editBusy = !!entryRequesting[editKey];
                  const delBusy = !!entryRequesting[delKey];
                  const deleting = !!entryDeleting[entryKey];
                   
                  return (
                    <React.Fragment key={en.id || en.entryId}>
                      <tr className={`entry-row ${isOpen ? 'expanded' : ''}`}>
                        <td>
                          <button
                            type="button"
                            className="expand-btn"
                          onClick={() => setExpanded((ex) => ({ ...ex, [entryKey]: !isOpen }))}
                          >
                            <span className={`expand-icon ${isOpen ? 'rotated' : ''}`}>›</span>
                          </button>
                        </td>
                        <td className="supplier-name">{en.supplierName}</td>
                        <td className="entry-date">{en.category || 'General'}</td>
                        <td className="entry-date">{formatDisplayDate(en.createdAt)}</td>
                        <td className="text-right quantity">{totalQty}</td>
                        <td className="text-right amount">{Number(en.total).toFixed(2)}</td>
                        {showSavedRetailTotals ? (
                          <td className="text-right quantity" style={RETAIL_ITEMS_CELL_STYLE}>
                            {totalRetailQty.toFixed(3)}
                          </td>
                        ) : null}
                        {showSavedRetailTotals ? (
                          <td className="text-right amount" style={RETAIL_ITEMS_CELL_STYLE}>
                            {totalRetailAmount.toFixed(2)}
                          </td>
                        ) : null}
                        {!lockedCategory ? (
                          <td className="text-right">
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => printEntry(en)}
                                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                              >
                                Print
                              </button>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => handleSendWhatsApp(en)}
                                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                              >
                                WhatsApp
                              </button>
                              {isAdmin ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => startEditEntry(en)}
                                    style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-remove"
                                    onClick={() => handleDeleteEntry(en)}
                                    disabled={deleting}
                                    style={{ width: 32, height: 32, padding: 0 }}
                                  >
                                    X
                                  </button>
                                </>
                              ) : (
                                <>
                                  {editStatus === "approved" ? (
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() => startEditEntry(en)}
                                      style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                    >
                                      Edit
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() => requestEntryAction(en, "edit")}
                                      disabled={editBusy || editStatus === "pending"}
                                      style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                      title={editStatus === "pending" ? "Edit request pending" : "Request edit"}
                                    >
                                      {editStatus === "pending" ? "Edit Pending" : "Request Edit"}
                                    </button>
                                  )}
                                  {delStatus === "approved" ? (
                                    <button
                                      type="button"
                                      className="btn-remove"
                                      onClick={() => handleDeleteEntry(en)}
                                      disabled={deleting}
                                      style={{ width: 32, height: 32, padding: 0 }}
                                    >
                                      X
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() => requestEntryAction(en, "delete")}
                                      disabled={delBusy || delStatus === "pending"}
                                      style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                      title={delStatus === "pending" ? "Delete request pending" : "Request delete"}
                                    >
                                      {delStatus === "pending" ? "Delete Pending" : "Request Delete"}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                      {isOpen && (
                        <tr className="expanded-content">
                          <td></td>
                          <td colSpan={!lockedCategory ? (showSavedRetailTotals ? 8 : 6) : (showSavedRetailTotals ? 7 : 5)}>
                            <div className="expansion-panel">
                              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap", marginBottom: 10 }}>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => printEntry(en)}
                                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                >
                                  Print
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => handleSendWhatsApp(en)}
                                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                >
                                  WhatsApp
                                </button>
                                {isAdmin ? (
                                  <>
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() => startEditEntry(en)}
                                      style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-remove"
                                      onClick={() => handleDeleteEntry(en)}
                                      disabled={!!entryDeleting[entryKey]}
                                      style={{ width: 32, height: 32, padding: 0 }}
                                    >
                                      X
                                    </button>
                                  </>
                                ) : (
                                  (() => {
                                    return (
                                      <>
                                        {editStatus === "approved" ? (
                                          <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => startEditEntry(en)}
                                            style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                          >
                                            Edit
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => requestEntryAction(en, "edit")}
                                            disabled={editBusy || editStatus === "pending"}
                                            style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                            title={editStatus === "pending" ? "Edit request pending" : "Request edit"}
                                          >
                                            {editStatus === "pending" ? "Edit Pending" : "Request Edit"}
                                          </button>
                                        )}

                                        {delStatus === "approved" ? (
                                          <button
                                            type="button"
                                            className="btn-remove"
                                            onClick={() => handleDeleteEntry(en)}
                                            disabled={!!entryDeleting[entryKey]}
                                            style={{ width: 32, height: 32, padding: 0 }}
                                          >
                                            X
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => requestEntryAction(en, "delete")}
                                            disabled={delBusy || delStatus === "pending"}
                                            style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                            title={delStatus === "pending" ? "Delete request pending" : "Request delete"}
                                          >
                                            {delStatus === "pending" ? "Delete Pending" : "Request Delete"}
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()
                                )}
                              </div>
                              <div className="items-section">
                                <h4 className="section-title">Items</h4>
                                {String(en?.category || "General") !== "General" && en.numberOfBoxes != null ? (
                                  <p><strong>Number of Boxes:</strong> {en.numberOfBoxes}</p>
                                ) : null}
                                <table className="items-table">
                                  <colgroup>
                                    <col style={{width: '120px'}} />
                                    <col style={{width: '200px'}} />
                                    {(en.category === "Stall" || en.category === "Market") ? <col style={{width: '90px'}} /> : null}
                                    {!(en.category === "Stall" || en.category === "Market") ? <col style={{width: '80px'}} /> : null}
                                    <col style={{width: '100px'}} />
                                    <col style={{width: '100px'}} />
                                    {(en.category === "Stall" || en.category === "Market") ? <col style={{width: '90px'}} /> : null}
                                    {(en.category === "Stall" || en.category === "Market") ? <col style={{width: '100px'}} /> : null}
                                    {(en.category === "Stall" || en.category === "Market") ? <col style={{width: '110px'}} /> : null}
                                    {(en.category === "Stall" || en.category === "Market") ? <col style={{width: '90px'}} /> : null}
                                    {(en.category === "Stall" || en.category === "Market") ? <col style={{width: '100px'}} /> : null}
                                    {(en.category === "Stall" || en.category === "Market") ? <col style={{width: '110px'}} /> : null}
                                  </colgroup>
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Material</th>
                                      {(en.category === "Stall" || en.category === "Market") ? (
                                        <th className="text-right" style={INWARD_ITEMS_HEADER_STYLE}>Stock Qty</th>
                                      ) : null}
                                      {!(en.category === "Stall" || en.category === "Market") ? (
                                        <th className="text-right">Qty</th>
                                      ) : null}
                                      <th className="text-right" style={(en.category === "Stall" || en.category === "Market") ? INWARD_ITEMS_HEADER_STYLE : undefined}>
                                        Rate
                                      </th>
                                      <th className="text-right" style={(en.category === "Stall" || en.category === "Market") ? INWARD_ITEMS_HEADER_STYLE : undefined}>
                                        Amount
                                      </th>
                                      {(en.category === "Stall" || en.category === "Market") ? (
                                        <th className="text-right" style={CLOSING_ITEMS_HEADER_STYLE}>Closing Qty</th>
                                      ) : null}
                                      {(en.category === "Stall" || en.category === "Market") ? (
                                        <th className="text-right" style={RETAIL_ITEMS_HEADER_STYLE}>Retail Qty</th>
                                      ) : null}
                                      {(en.category === "Stall" || en.category === "Market") ? (
                                        <th className="text-right" style={RETAIL_ITEMS_HEADER_STYLE}>Retail Rate</th>
                                      ) : null}
                                      {(en.category === "Stall" || en.category === "Market") ? (
                                        <th className="text-right" style={RETAIL_ITEMS_HEADER_STYLE}>Retail Amount</th>
                                      ) : null}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {en.items.map((it, idx) => (
                                      <tr key={idx}>
                                        <td>{formatDisplayDate(it.date)}</td>
                                        <td>{it.material}</td>
                                        {(en.category === "Stall" || en.category === "Market") ? (
                                          <td className="text-right" style={INWARD_ITEMS_CELL_STYLE}>{Number(it.stockQty || it.stock_qty || 0).toFixed(3)}</td>
                                        ) : null}
                                        {!(en.category === "Stall" || en.category === "Market") ? (
                                          <td className="text-right">{it.qty}</td>
                                        ) : null}
                                        <td className="text-right" style={(en.category === "Stall" || en.category === "Market") ? INWARD_ITEMS_CELL_STYLE : undefined}>
                                          {Number(it.rate).toFixed(2)}
                                        </td>
                                        <td className="text-right" style={(en.category === "Stall" || en.category === "Market") ? INWARD_ITEMS_CELL_STYLE : undefined}>
                                          {Number(it.amount).toFixed(2)}
                                        </td>
                                        {(en.category === "Stall" || en.category === "Market") ? (
                                          <td className="text-right" style={CLOSING_ITEMS_CELL_STYLE}>{Number(it.closingQty || it.closing_qty || 0).toFixed(3)}</td>
                                        ) : null}
                                        {(en.category === "Stall" || en.category === "Market") ? (
                                          <td className="text-right" style={RETAIL_ITEMS_CELL_STYLE}>{readRetailQty(it).toFixed(3)}</td>
                                        ) : null}
                                        {(en.category === "Stall" || en.category === "Market") ? (
                                          <td className="text-right" style={RETAIL_ITEMS_CELL_STYLE}>{readRetailRate(it).toFixed(2)}</td>
                                        ) : null}
                                        {(en.category === "Stall" || en.category === "Market") ? (
                                          <td className="text-right" style={RETAIL_ITEMS_CELL_STYLE}>{readRetailAmount(it).toFixed(2)}</td>
                                        ) : null}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              </table>
            </div>
            )}
            <div
              style={{
                padding: "0 1.5rem 1.25rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: "0.82rem", color: "#475569" }}>
                {`Showing ${savedEntriesPageStart}-${savedEntriesPageEnd} of ${activeSavedEntries.length} entries`}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setSavedEntriesPage((prev) => Math.max(1, prev - 1))}
                  disabled={savedEntriesPage <= 1}
                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                >
                  Prev
                </button>
                <span className="entry-count">{`Page ${savedEntriesPage} / ${activeSavedEntriesTotalPages}`}</span>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setSavedEntriesPage((prev) => Math.min(activeSavedEntriesTotalPages, prev + 1))}
                  disabled={savedEntriesPage >= activeSavedEntriesTotalPages}
                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showSupplierSummaryTools && supplierDetailsModal.open && savedSupplierFilter ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={closeSupplierDetailsModal}>
          <div
            className="modal-card"
            style={{ width: "min(860px, 100%)", maxHeight: "85vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-title">
              Supplier Date-wise Details - {savedSupplierFilter}
            </div>
            <div className="modal-body">
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: 12,
                  fontSize: "0.85rem",
                }}
              >
                <span style={{ color: "#475569" }}>
                  Opened from:{" "}
                  <strong>
                    {supplierDetailsModal.metric === "qty"
                      ? "Total Qty"
                      : supplierDetailsModal.metric === "amount"
                        ? "Total Amount"
                        : "Total Paid"}
                  </strong>
                </span>
                {paidOnlyModal ? (
                  <span style={{ marginLeft: "auto", fontWeight: 700, color: "#16a34a" }}>
                    Paid: {Number(selectedSupplierTotals?.paid || 0).toFixed(2)}
                  </span>
                ) : (
                  <>
                    <span style={{ marginLeft: "auto", fontWeight: 700 }}>
                      Qty: {Number(selectedSupplierTotals?.qty || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700 }}>
                      Amount: {Number(selectedSupplierTotals?.amount || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700, color: "#16a34a" }}>
                      Paid: {Number(selectedSupplierTotals?.paid || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700, color: "#b91c1c" }}>
                      Balance: {Number(selectedSupplierTotals?.balance || 0).toFixed(2)}
                    </span>
                  </>
                )}
              </div>

              {paidOnlyModal ? (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: 12,
                    padding: "10px",
                    border: "1px solid #dbeafe",
                    background: "#f8fbff",
                    borderRadius: 10,
                  }}
                >
                  <input
                    type="date"
                    className="input-field compact"
                    max={new Date().toISOString().slice(0, 10)}
                    value={supplierPaidForm.date}
                    onChange={(e) => setSupplierPaidForm((prev) => ({ ...prev, date: e.target.value }))}
                    style={{ width: 160 }}
                    disabled={supplierPaidSaving}
                  />
                  <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    className="input-field compact numeric"
                    placeholder="Paid amount"
                    value={supplierPaidForm.amount}
                    onChange={(e) => setSupplierPaidForm((prev) => ({ ...prev, amount: e.target.value }))}
                    style={{ width: 180 }}
                    disabled={supplierPaidSaving}
                  />
                  <button
                    type="button"
                    className="btn-add-payment"
                    onClick={handleAddSupplierPaidFromModal}
                    disabled={supplierPaidSaving}
                  >
                    {supplierPaidSaving ? "Saving..." : "Add Paid Amount"}
                  </button>
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: "0.85rem", color: "#475569" }}>
                  Showing {supplierDetailsPageStart}-{supplierDetailsPageEnd} of {supplierDetailsRowsForModal.length}
                </div>
                {supplierDetailsTotalPages > 1 ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setSupplierDetailsPage((p) => Math.max(1, p - 1))}
                      disabled={supplierDetailsPage <= 1}
                      style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                    >
                      Prev
                    </button>
                    <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>
                      Page {supplierDetailsPage} / {supplierDetailsTotalPages}
                    </span>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setSupplierDetailsPage((p) => Math.min(supplierDetailsTotalPages, p + 1))}
                      disabled={supplierDetailsPage >= supplierDetailsTotalPages}
                      style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                    >
                      Next
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="entries-table-container">
                <table className="entries-table" style={{ minWidth: paidOnlyModal ? 520 : 720 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      {!paidOnlyModal ? <th className="text-right">Entries</th> : null}
                      {!paidOnlyModal ? (
                        <th
                          className="text-right"
                          style={
                            supplierDetailsModal.metric === "qty"
                              ? { background: "#1d4ed8", color: "#fff", fontWeight: 800 }
                              : undefined
                          }
                        >
                          Total Qty
                        </th>
                      ) : null}
                      {!paidOnlyModal ? (
                        <th
                          className="text-right"
                          style={
                            supplierDetailsModal.metric === "amount"
                              ? { background: "#7c3aed", color: "#fff", fontWeight: 800 }
                              : undefined
                          }
                        >
                          Total Amount
                        </th>
                      ) : null}
                      {!amountModal ? (
                        <th
                          className="text-right"
                          style={
                            supplierDetailsModal.metric === "paid"
                              ? { background: "#059669", color: "#fff", fontWeight: 800 }
                              : undefined
                          }
                        >
                          Total Paid
                        </th>
                      ) : null}
                      {paidOnlyModal ? <th style={{ width: 230 }}>Actions</th> : null}
                      {!paidOnlyModal ? <th className="text-right">Balance</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {supplierDetailsPagedRows.length === 0 ? (
                      <tr>
                        <td colSpan={supplierDetailsColSpan} className="empty-state" style={{ padding: "12px" }}>
                          No data for selected supplier
                        </td>
                      </tr>
                    ) : (
                      supplierDetailsPagedRows.map((row, rowIndex) => (
                        <tr key={row.paymentId || `${row.date}-${rowIndex}`}>
                          <td>
                            {paidOnlyModal && supplierPaidEdit.paymentId === row.paymentId ? (
                              <input
                                type="date"
                                className="input-field compact"
                                max={new Date().toISOString().slice(0, 10)}
                                value={supplierPaidEdit.date}
                                onChange={(e) => setSupplierPaidEdit((prev) => ({ ...prev, date: e.target.value }))}
                                style={{ width: 160 }}
                                disabled={supplierPaidUpdating}
                              />
                            ) : (
                              formatDisplayDate(row.date)
                            )}
                          </td>
                          {!paidOnlyModal ? <td className="text-right">{row.entryCount}</td> : null}
                          {!paidOnlyModal ? (
                            <td
                              className="text-right"
                              style={supplierDetailsModal.metric === "qty" ? { background: "#eff6ff", fontWeight: 700 } : undefined}
                            >
                              {Number(row.totalQty || 0).toFixed(2)}
                            </td>
                          ) : null}
                          {!paidOnlyModal ? (
                            <td
                              className="text-right"
                              style={supplierDetailsModal.metric === "amount" ? { background: "#eff6ff", fontWeight: 700 } : undefined}
                            >
                              {Number(row.totalAmount || 0).toFixed(2)}
                            </td>
                          ) : null}
                          {!amountModal ? (
                            <td
                              className="text-right"
                              style={supplierDetailsModal.metric === "paid" ? { background: "#ecfdf5", fontWeight: 700 } : { color: "#16a34a" }}
                            >
                              {paidOnlyModal && supplierPaidEdit.paymentId === row.paymentId ? (
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="any"
                                  className="input-field compact numeric"
                                  value={supplierPaidEdit.amount}
                                  onChange={(e) => setSupplierPaidEdit((prev) => ({ ...prev, amount: e.target.value }))}
                                  style={{ width: 140, marginLeft: "auto" }}
                                  disabled={supplierPaidUpdating}
                                />
                              ) : (
                                Number(row.totalPaid || 0).toFixed(2)
                              )}
                            </td>
                          ) : null}
                          {paidOnlyModal ? (
                            <td>
                              {supplierPaidEdit.paymentId === row.paymentId ? (
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button
                                    type="button"
                                    className="btn-add-payment"
                                    onClick={handleSaveEditSupplierPaid}
                                    disabled={supplierPaidUpdating}
                                    style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                  >
                                    {supplierPaidUpdating ? "Saving..." : "Save"}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleCancelEditSupplierPaid}
                                    disabled={supplierPaidUpdating}
                                    style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => handleStartEditSupplierPaid(row)}
                                    disabled={!row.paymentId || supplierPaidUpdating || !!supplierPaidDeleting[row.paymentId]}
                                    style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => handleDeleteSupplierPaid(row)}
                                    disabled={!row.paymentId || supplierPaidUpdating || !!supplierPaidDeleting[row.paymentId]}
                                    style={{
                                      padding: "0.3rem 0.7rem",
                                      fontSize: "0.75rem",
                                      borderColor: "#fecaca",
                                      color: "#b91c1c",
                                      background: "#fff5f5",
                                    }}
                                  >
                                    {supplierPaidDeleting[row.paymentId] ? "Deleting..." : "Delete"}
                                  </button>
                                </div>
                              )}
                            </td>
                          ) : null}
                          {!paidOnlyModal ? (
                            <td className="text-right" style={{ color: Number(row.balance || 0) > 0 ? "#b91c1c" : "#16a34a" }}>
                              {Number(row.balance || 0).toFixed(2)}
                            </td>
                          ) : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeSupplierDetailsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
